import HelpOutlineIcon from '@material-ui/icons/HelpOutline'
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp'
import { makeStyles } from '@material-ui/styles'
import { insertAll, keys, pick, prop } from 'ramda'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import useReactRouter from 'use-react-router'
import { listTenants } from 'account/components/userManagement/tenants/new-actions'
import { tenantsSelector } from 'account/components/userManagement/tenants/selectors'
import { generateApiErrorPayload } from 'api-client/helpers'
import { listTablePrefs, type TablePrefsParams } from 'app/constants'
import { canManageInfrastructure, isSelfServiceRole, userHasRole } from 'app/core/utils/rbac'
import { isAdmin } from 'app/plugins/infrastructure/components/common/helpers'
import { completedStatuses } from 'app/plugins/openstack/components/infrastructure/hosts/host-details/MaintenanceModeStatusBar'
import ChangeOwnerDialog from 'app/plugins/openstack/components/vms/ChangeOwnerDialog'
import ChangeTenantDialog from 'app/plugins/openstack/components/vms/ChangeTenantDialog'
// import OldDeleteVirtualMachineDialog from 'app/plugins/openstack/components/vms/OldDeleteVirtualMachineDialog'
import DeleteVirtualMachineDialog from 'app/plugins/openstack/components/vms/DeleteVirtualMachineDialog'
import EditVmPortSecurityModal from 'app/plugins/openstack/components/vms/EditVmPortSecurityModal'
import { useVirtualMachinesPagination } from 'app/plugins/openstack/components/vms/useVirtualMachinesPagination'
import { useVMFilterConfigs } from 'app/plugins/openstack/components/vms/useVMFilterConfigs'
import store, { type RootState } from 'app/store'
import { trackChameleonEvent } from 'app/utils/tracking'
import type { ArrayElement } from 'core/actions/Action'
import type InferActionParams from 'core/actions/InferActionParams'
import PollingData from 'core/components/PollingData'
import SimpleLink from 'core/components/SimpleLink'
import ServerSideSearch, {
  type ServerSideSearchParams,
} from 'core/components/server-side-pagination/ServerSideSearch'
import TitleWithInfoIcon from 'core/components/TitleWithInfoIcon'
import ListContainer from 'core/containers/ListContainer'
import GridDefaultActionButton, {
  type GridDefaultActionButtonProps,
} from 'core/elements/grid/buttons/GridDefaultActionButton'
import { createGridLinkCell } from 'core/elements/grid/cells/GridLinkCell'
import type { GridViewColumn } from 'core/elements/grid/Grid'
import { useGridContext } from 'core/elements/grid/Grid'
import getGridDialogButton from 'core/elements/grid/helpers/getGridDialogButton'
import type { GridRowMenuItemSpec } from 'core/elements/grid/hooks/useGridRowMenu'
import type {
  GridBatchActionSpec,
  GridDropdownBatchActionGroup,
} from 'core/elements/grid/hooks/useGridSelectableRows'
import type { SortingState } from 'core/elements/grid/hooks/useGridSorting'
import Text from 'core/elements/Text'
import ToggleSwitch from 'core/elements/ToggleSwitch'
import Tooltip from 'core/elements/tooltip/Tooltip'
import useGlobalParams from 'core/hooks/useGlobalParams'
import useListAction from 'core/hooks/useListAction'
import { createUsePrefParamsHook } from 'core/hooks/useParams'
import useUpdateAction from 'core/hooks/useUpdateAction'
import {
  type ChameleonReducer,
  chameleonActions,
  chameleonKey,
} from 'core/session/chameleonReducers'
import { type SessionState, sessionActions, sessionStoreKey } from 'core/session/sessionReducers'
import useScopedPreferences from 'core/session/useScopedPreferences'
import type Theme from 'core/themes/model'
import { routeManager } from 'core/utils/routes'
import { createResourceLabelsCell } from 'k8s/components/common/entity/labels-and-annotations/helpers'
import DataKeys from 'k8s/DataKeys'
import { getBlueprints, listResmgrHosts } from 'openstack/components/infrastructure/actions'
import {
  listClusterHostGroups,
  listClusterVMHAStatus,
} from 'openstack/components/infrastructure/clusters/actions'
import { clusterHostGroupsSelector } from 'openstack/components/infrastructure/clusters/selectors'
import { VMHAStatusCellForVMsList } from 'openstack/components/infrastructure/clusters/VMHAStatusCell'
import { IHostDetailsPageTabs } from 'openstack/components/infrastructure/hosts/host-details/model'
import { resmgrHostsSelector } from 'openstack/components/infrastructure/selectors'
import { IVolumeDetailsPageTabs } from 'openstack/components/storage/volumes/volume-details/model'
import AddFixedIpModal from 'openstack/components/vms/AddFixedIpModal'
import AddFloatingIpDialog from 'openstack/components/vms/AddFloatingIpDialog'
import AssignStretchedClusterDialog from 'openstack/components/vms/AssignStretchedClusterDialog'
import {
  anyVmDependencyNotLoaded,
  deleteVirtualMachine,
  getConsoleEndpoint,
  getNovaVersion,
  type listVirtualMachines,
  refreshVmDependencies,
} from 'openstack/components/vms/actions'
import CloneVirtualMachineModal from 'openstack/components/vms/CloneVirtualMachineModal'
import ConfirmResizeDialog from 'openstack/components/vms/ConfirmResizeDialog'
import { openConsoleWindow } from 'openstack/components/vms/consoleWindow'
import EditVmMetadataModal from 'openstack/components/vms/EditVmMetadataModal'
import EditVmSecurityGroupsModal from 'openstack/components/vms/EditVmSecurityGroupsModal'
import FailedMigrationReasonDialog from 'openstack/components/vms/FailedMigrationReasonDialog'
import HotplugVmDialog from 'openstack/components/vms/HotplugVmDialog'
import CustomVmLeaseModal from 'openstack/components/vms/leases/CustomVmLeaseModal'
import ManageVolumesModal from 'openstack/components/vms/ManageVolumesModal'
import ManageVolumeTerminationModal from 'openstack/components/vms/ManageVolumeTerminationModal'
import MigrateVirtualMachineDialog from 'openstack/components/vms/MigrateVirtualMachineDialog'
import { getMigrationsSince, listMigrations } from 'openstack/components/vms/migrations/actions'
import type { VmVtpm } from 'openstack/components/vms/newModel'
import PowerVirtualMachineDialog from 'openstack/components/vms/PowerVirtualMachineDialog'
import RebuildVirtualMachineDialog from 'openstack/components/vms/RebuildVirtualMachineDialog'
import RemoveFixedIpDialog from 'openstack/components/vms/RemoveFixedIpDialog'
import RemoveFloatingIpDialog from 'openstack/components/vms/RemoveFloatingIpDialog'
import RemoveStretchedClusterDialog from 'openstack/components/vms/RemoveStretchedClusterDialog'
import RenameVirtualMachineDialog from 'openstack/components/vms/RenameVirtualMachineDialog'
import RescueVmDialog from 'openstack/components/vms/RescueVmDialog'
import ResizeVirtualMachineDialog from 'openstack/components/vms/ResizeVirtualMachineDialog'
import SnapshotVirtualMachineDialog from 'openstack/components/vms/SnapshotVirtualMachineDialog'
import type { virtualMachinesSelector } from 'openstack/components/vms/selectors'
import { virtualMachinesByIdSelector } from 'openstack/components/vms/selectors'
import UnrescueVmDialog from 'openstack/components/vms/UnrescueVmDialog'
import UpdateMigrationPriorityDialog from 'openstack/components/vms/UpdateMigrationPriorityDialog'
import VMServerSidePagination from 'openstack/components/vms/VMServerSidePagination'
import VmErrorModal from 'openstack/components/vms/VmErrorModal'
import VmhaProtectionStatusInfo from 'openstack/components/vms/VmhaProtectionStatusInfo'
import VmStateCellComponent from 'openstack/components/vms/VmStateCellComponent'
import { IVmDetailsPageTabs } from 'openstack/components/vms/vm-details/model'
import { hasPermissions, humanReadableSize } from 'openstack/helpers'
import { formatTableDate } from 'utils/misc'
import { STRETCHED_CLUSTER_METADATA_KEY } from '../infrastructure/stretched-clusters/constants'
import AddServerGroupDialog from './AddServerGroupDialog'
import CreateVirtualMachineModal from './CreateVirtualMachineModal'
import {
  defaultVMFilterParams,
  VM_COLUMN_TO_SORT_KEY,
  VM_SEARCH_FIELDS,
  VM_SEARCH_FIELDS_ENUM,
  VM_SORT_KEY_TO_COLUMN,
  VM_SORT_OPTIONS,
} from './constants'
import GetDisabledTooltipForVMs, { canMigrateVMs } from './GetDisabledTooltipForVMs'
import {
  canChangeTenantVms,
  canConfirmResizeVm,
  canConfirmResizeVms,
  canDeleteVms,
  canHardReboot,
  canHotplugVm,
  canHotplugVms,
  canResizeVm,
  canResizeVms,
  canSoftReboot,
  getConfirmResizeVmsTooltip,
  getConfirmResizeVmTooltip,
  getExtraDriveVolumesInfo,
  getHotplugVmsTooltip,
  getResizeVmsTooltip,
  getResizeVmTooltip,
  isHotplugVm,
  isVolumeBackedVm,
} from './helpers'
import { VM_ISO_OPERATION_COMPLETED_EVENT } from './isoMountEvents'
import { options } from './MigrationPriorityPicklist'
import MountISODialog from './MountISODialog'
import RemoveServerGroupDialog from './RemoveServerGroupDialog'
import { VALID_SERVER_GROUP_VM_STATES } from './server-groups/helpers'
import {
  ACTION_LIMITATION_TOOLTIPS,
  TOOLTIP_MESSAGES,
  VOLUME_BACKED_VM_ERROR_MSG,
} from './tooltipConstants'
import UnmountISODialog from './UnmountISODialog'
import {
  getDiscoveredPortBindings,
  groupL2IpsByNetworkName,
  isVMUsingOnlySimpleNetwork,
  stripDiscoveredPortBindings,
} from './utils'
import {
  canIsoMount,
  canUnMountIso,
  disabledUnMountIsoTooltip,
  disableMountIsoTooltip,
} from './utils/isoHelper'
import VmConsoleLoadingDialog from './VmConsoleLoadingDialog'

type ModelDataKey = DataKeys.OpenstackVirtualMachines
type SelectorModel = ArrayElement<ReturnType<typeof virtualMachinesSelector>>
type ActionParams = InferActionParams<typeof listVirtualMachines>
// @fixme using a type here because of https://github.com/microsoft/TypeScript/issues/15300
type Params = ActionParams & {}
const defaultParams: Params & SortingState = {
  orderBy: 'created',
  orderDirection: 'desc',
}

const leaseActionText = {
  delete: 'Delete',
  'power off': 'Stop',
}

const usePrefParams = createUsePrefParamsHook<Params & TablePrefsParams>(
  'OpenstackVirtualMachines',
  listTablePrefs,
)

const L2IpWithTooltip = ({ ip }: { ip: string }) => {
  const classes = useStyles()
  return (
    <div className={classes.l2IpRow}>
      <div>{ip}</div>
      <Tooltip message="Static or External DHCP IP">
        <HelpOutlineIcon className={classes.l2IpIcon} />
      </Tooltip>
    </div>
  )
}

export const IpAddressesCellComponent = ({ value, item }) => {
  const classes = useStyles()

  const ipAddressKeys = keys(value)

  // L2 IPs (discovered, stored on metadata) grouped by the network name each port carries.
  // Anything we can't attribute (no matching port, or a port without a network name) comes
  // back as `unresolved`. [FR-011]
  const { resolved, unresolved } = groupL2IpsByNetworkName(item)

  // Fallback for unresolved IPs only: keep the old single-empty-network heuristic, then
  // bottom-dump, so old data without ports/network names behaves as before. [FR-011]
  const emptyNetworkKeys = unresolved.length
    ? ipAddressKeys.filter((key: string) => value[key].length === 0 && !resolved[key])
    : []
  const l2NetworkKey = emptyNetworkKeys.length === 1 ? emptyNetworkKeys[0] : undefined

  const unresolvedRows = unresolved.map((ip) => <L2IpWithTooltip key={ip} ip={ip} />)

  return (
    <div className={classes.addresses}>
      {ipAddressKeys?.map((key: string) => {
        return (
          <div key={key}>
            <div>
              <b>{key}</b>
            </div>
            {value[key].map((address) => (
              <div key={address.addr}>{address.addr}</div>
            ))}
            {(resolved[key] || []).map((ip) => (
              <L2IpWithTooltip key={ip} ip={ip} />
            ))}
            {key === l2NetworkKey && unresolvedRows}
          </div>
        )
      })}
      {/* Fallback: render any still-unattributed L2 IPs at the bottom (matches prior behavior). */}
      {unresolved.length > 0 && !l2NetworkKey && unresolvedRows}
    </div>
  )
}

const TenantCellComponent = ({ value, item }) => {
  useListAction(listTenants)
  const tenants = useSelector(tenantsSelector)
  const tenant = tenants.find((ten) => ten.id === value)
  return <Text variant="body2">{tenant?.name || tenant?.id || value}</Text>
}

const VolumesCellComponent = ({ value, item }) => {
  const [expanded, setExpanded] = useState(false)
  const classes = useStyles()

  const volumeIds = item?.volumeIds as string[] | undefined
  const volumeDetails = item?.volumeDetails

  if (!volumeIds?.length) return null

  const firstVolumeId = volumeIds[0]
  const remainingCount = volumeIds.length - 1
  const visibleIds = expanded ? volumeIds : [firstVolumeId]

  return (
    <div className={classes.volumesCell}>
      {visibleIds.map((volumeId) => (
        <SimpleLink
          key={volumeId}
          src={routeManager.routes.openstack.volumeDetails.path({
            id: volumeId,
            tab: IVolumeDetailsPageTabs.Overview,
          })}
        >
          {volumeDetails?.[volumeId]?.volume_name || volumeDetails?.[volumeId]?.name || volumeId}
        </SimpleLink>
      ))}
      {remainingCount > 0 && (
        <button
          className={classes.volumesExpander}
          onClick={(e) => {
            e.stopPropagation()
            setExpanded((prev) => !prev)
          }}
        >
          <Text variant="body2" className={classes.volumesExpanderText}>
            {expanded ? 'Collapse' : `Show ${remainingCount} more`}
          </Text>
          {expanded ? (
            <KeyboardArrowUpIcon fontSize="small" className={classes.volumesExpanderIcon} />
          ) : (
            <KeyboardArrowDownIcon fontSize="small" className={classes.volumesExpanderIcon} />
          )}
        </button>
      )}
    </div>
  )
}

export const MetadataCellComponent = ({ value }) => {
  // Hide the IP-discovery port bindings; they're shown as IP addresses, not labels. [FR-024]
  const filteredMetadata = stripDiscoveredPortBindings(value)

  const ResourceLabelsCell = createResourceLabelsCell({ type: 'table', separator: '=' })
  return <ResourceLabelsCell value={filteredMetadata} />
}

const vtpmSourceLabels = {
  flavor: 'Flavor',
  image: 'Image',
  'flavor+image': 'Flavor + Image',
}

// FR-043: vTPM status; '-' when the backend omitted the fields (names-only listings)
export const VtpmCellComponent = ({ value, item }) => {
  if (value === undefined || value === null) {
    return <Text variant="body2">-</Text>
  }
  if (!value) {
    return <Text variant="body2">Disabled</Text>
  }
  const { version, model, source } = (item?.vtpm || {}) as Partial<VmVtpm>
  const sourceLabel = vtpmSourceLabels[source] || source
  return (
    <Tooltip message={`Version: ${version}, Model: ${model}, Source: ${sourceLabel}`}>
      <Text variant="body2">{version ? `Enabled (TPM ${version})` : 'Enabled'}</Text>
    </Tooltip>
  )
}

export const vmColumns = ({
  setErrorVm,
  setMigrationFailureVm,
  userRole,
  hosts,
  allTenants,
  blueprint,
  clusterHostGroups = [],
}): GridViewColumn<SelectorModel>[] => {
  const columns = [
    {
      key: 'name',
      label: 'Name',
      width: 'medium',
      CellComponent: createGridLinkCell({
        routeToFn: ({ id }) =>
          routeManager.routes.openstack.vmDetails.path({ id, tab: IVmDetailsPageTabs.Overview }),
      }),
    },
    {
      key: 'description',
      label: 'Description',
      display: false,
      disableSorting: true,
    },
    {
      key: 'links',
      label: 'Tasks',
      disableSorting: true,
      render: (val, item) => (
        <SimpleLink
          src={routeManager.routes.openstack.vmDetails.path({
            id: item?.id,
            tab: IVmDetailsPageTabs.Overview,
          })}
        >
          View Tasks
        </SimpleLink>
      ),
    },
    {
      key: 'id',
      label: 'UUID',
      // PCD-8088: OpenStack permits duplicate VM names within a tenant; keeping
      // the UUID column visible by default lets operators disambiguate them.
      display: true,
    },
    {
      key: 'state',
      label: 'State',
      CellComponent: (props) => (
        <VmStateCellComponent
          {...props}
          setErrorVm={setErrorVm}
          setMigrationFailureVm={setMigrationFailureVm}
          hosts={hosts}
        />
      ),
    },
    {
      // FR-039: Per-VM HA protection status, derived from the VM's cluster HA
      // status downgraded when this VM's own storage is at risk (see FR-VSC-005).
      // FR-041: status meanings shown as a single legend in the header tooltip.
      key: 'vmhaStatus',
      label: (
        <TitleWithInfoIcon
          titleText="VM HA Protection Status"
          tooltipMsg={<VmhaProtectionStatusInfo />}
        />
      ),
      disableSorting: true,
      CellComponent: (props) => (
        <VMHAStatusCellForVMsList
          {...props}
          clusterHostGroups={clusterHostGroups}
          blueprint={blueprint}
        />
      ),
    },
    {
      key: 'ownerName',
      label: 'Owner',
    },
    {
      key: 'clusterName',
      label: 'Cluster',
      CellComponent: createGridLinkCell({
        routeToFn: ({ clusterName }) =>
          clusterName
            ? routeManager.routes.openstack.clusterDetails.path({ id: clusterName })
            : null,
      }),
    },
    ...(isAdmin()
      ? [
          {
            key: 'stretchedClusterName',
            label: 'Failure Domain',
            render: (value) => <Text variant="body2">{value ? value : '-'}</Text>,
            disableSorting: true,
          },
        ]
      : []),
    {
      key: 'tenantName',
      label: 'Tenant',
      CellComponent: TenantCellComponent,
    },
    {
      key: 'addresses',
      label: 'IP Addresses',
      CellComponent: IpAddressesCellComponent,
      disableSorting: true,
    },
    {
      key: 'flavorName',
      label: 'Flavor',
      render: (value) => <Text variant="body2">{value}</Text>,
    },
    // FR-042: vCPUs, RAM and Disk sort server-side via VM_COLUMN_TO_SORT_KEY.
    {
      key: 'flavorVcpus',
      label: 'vCPUs',
    },
    {
      key: 'flavorRam',
      label: 'RAM',
      render: (ram) => humanReadableSize(ram * 1024 * 1024),
    },
    {
      key: 'flavorDisk',
      label: 'Disk',
      render: (disk) => humanReadableSize(disk * 1024 * 1024 * 1024),
    },
    {
      key: 'imageName',
      label: 'Source Image',
    },
    {
      key: 'volumeNames',
      label: 'Volumes',
      CellComponent: VolumesCellComponent,
      memoizeCell: false,
      disableSorting: true,
    },
    {
      key: 'isoAttachment',
      label: 'Attached ISO',
      disableSorting: true,
      render: (val, item) => {
        const { volumes: cdromVolumes } = getExtraDriveVolumesInfo(item)

        if (!cdromVolumes.length) {
          return <Text variant="body2">-</Text>
        }

        return (
          <div>
            {cdromVolumes.map((v: any) => (
              <Tooltip key={v?.id} message={`ISO: ${v?.name || v?.id}`}>
                <Text variant="body2">{v?.name || v?.id}</Text>
              </Tooltip>
            ))}
          </div>
        )
      },
      display: false,
    },
    {
      key: 'created',
      label: 'Created',
      formatFn: (value) => formatTableDate(value),
    },
    {
      key: 'securityGroupNames',
      label: 'Security Groups',
      render: (groups) => groups?.join(', '),
      disableSorting: true,
    },
    {
      key: 'serverGroup',
      label: 'Server Group',
      render: (group) => (group ? `${group?.name} (${group?.policy})` : ''),
      disableSorting: true,
    },
    {
      key: 'OS-EXT-SRV-ATTR:root_device_name',
      disableSorting: true,
      label: 'Root Disk Type',
      render: (name, item) => {
        return isVolumeBackedVm(item)
          ? 'Volume'
          : blueprint?.instanceSharedStorage
            ? 'Ephemeral Shared Storage'
            : 'Ephemeral Local Storage'
      },
    },
    {
      key: 'os-extended-volumes:volumes_attached',
      label: 'Volume Delete on VM Termination',
      disableSorting: true,
      render: (val) => {
        return val?.[0]?.delete_on_termination === true ? 'Enabled' : 'Disabled'
      },
    },
    {
      key: 'updated',
      label: 'Updated At',
      display: false,
      formatFn: (value) => (value ? formatTableDate(value) : null),
    },
    {
      key: 'launched_at',
      label: 'Launched At',
      display: false,
      formatFn: (value) => (value ? formatTableDate(value) : null),
    },
    {
      key: 'terminated_at',
      label: 'Terminated At',
      display: false,
      formatFn: (value) => (value ? formatTableDate(value) : null),
    },
    ...(allTenants
      ? []
      : [
          {
            key: 'expirationTime',
            label: 'Lease Expiration',
            render: (value, item) =>
              value ? `${leaseActionText[item?.leaseAction]} at ${formatTableDate(value)}` : null,
            disableSorting: true,
          },
        ]),
    {
      key: 'migrationPriority',
      label: 'Migration Priority',
      display: false,
      render: (value) =>
        options?.find((option) => option?.value === value)?.label || options[0]?.label,
      disableSorting: true,
    },
    {
      // FR-043: second-to-last column, immediately before Metadata
      key: 'vtpm_enabled',
      label: 'vTPM',
      disableSorting: true,
      CellComponent: VtpmCellComponent,
    },
    {
      key: 'metadata',
      label: 'Metadata',
      disableSorting: true,
      CellComponent: MetadataCellComponent,
    },
  ]
  if (canManageInfrastructure()) {
    // @ts-ignore
    // FR-039: index bumped 5 -> 6 so the admin-only Host column lands after
    // vmhaStatus instead of between State and VM HA Protection Status.
    return insertAll(
      6,
      [
        {
          key: 'OS-EXT-SRV-ATTR:hypervisor_hostname',
          label: 'Host',
          // Todo: cell component with link
          CellComponent: createGridLinkCell({
            routeToFn: (props) => {
              const hostId = props['OS-EXT-SRV-ATTR:host']
              if (!hostId) {
                return null
              }
              return routeManager.routes.openstack.hostDetails.path({
                id: hostId,
                tab: IHostDetailsPageTabs.Overview,
              })
            },
          }),
        },
      ],
      columns,
    )
  }
  // @ts-ignore
  return columns
}

export const chameleonVmSurveyCheck = (vms, vmId, dispatch) => {
  const chameleonVmIsActive = vms?.some((vm) => vm?.id === vmId && vm?.state === 'active')
  // Show survey & clear created vm id
  if (chameleonVmIsActive) {
    trackChameleonEvent('showVmCreationSurvey', 10)
    dispatch(
      chameleonActions.updateChameleonState({
        createdVmId: null,
      }),
    )
  }
}

export const chameleonVmMigrationSurveyCheck = (vms, vmIds, dispatch) => {
  const chameleonMigrationSuccess = vms?.some(
    (vm) => vmIds.includes(vm?.id) && completedStatuses.includes(vm?.migration?.status),
  )
  // Show survey & clear migrated vm ids
  if (chameleonMigrationSuccess) {
    trackChameleonEvent('showVmMigrationSurvey', 33)
    dispatch(
      chameleonActions.updateChameleonState({
        migratedVmIds: [],
      }),
    )
  }
}

// CLVM-FR-015: Clone VM's batch (toolbar) action navigates to the route-driven wizard instead of
// toggling a local dialog-open boolean like getGridDialogButton does. Syncs the grid's current
// selection into the caller's `rows` state before navigating (kept for same-render-cycle display),
// but the real source of truth once navigated is the vmId baked into `buildPath`'s url by the
// caller — see the VirtualMachinesList-fix comment below for why local state alone isn't enough.
function getGridNavigationButton<T>(
  buildPath: (selectedItems: T[]) => string,
  onSelect: (selectedItems: T[]) => void,
  customButtonProps: Partial<GridDefaultActionButtonProps<T>> = {},
) {
  return function GridNavigationButton({
    closeDropdown,
    ...buttonProps
  }: GridDefaultActionButtonProps<T>) {
    const { selectedItems } = useGridContext<T>()
    const { history } = useReactRouter()
    const handleClick = useCallback(() => {
      onSelect(selectedItems)
      closeDropdown && closeDropdown()
      history.push(buildPath(selectedItems))
    }, [selectedItems, closeDropdown, history, onSelect, buildPath])

    return <GridDefaultActionButton {...buttonProps} {...customButtonProps} onClick={handleClick} />
  }
}

/**
 * VirtualMachinesList - Displays VMs for a specific host
 * This is a common component used by VirtualMachinesListPage and HostDetailsPage
 * It includes all the common functionality for VMs list including Pagination, Sorting, Filtering, etc.
 * All the Actions - Batch Actions, Row Actions, are also included in this component
 */
export default function VirtualMachinesList({ host = null, pausePolling = false }) {
  const {
    allParams: params,
    updateGlobalParams,
    getParamsUpdater,
  } = useGlobalParams(usePrefParams, defaultParams)
  const { history, location } = useReactRouter()
  const classes = useStyles()
  // CLVM-FR-015 fix: this component is reused host-scoped inside HostDetailsPage (`host` set) and
  // standalone on the plain VM list page (`host` null). Each registered route gets its own React
  // key (`<Route key={link.path}>` in core/plugins/helpers.tsx), so navigating from this page's own
  // route to its `/clone` sub-route is a DIFFERENT Switch entry and fully unmounts/remounts this
  // component — wiping any local state (including `selectedVms`) the instant Clone is clicked, even
  // on the plain VM list page itself, not just host-scoped. Fixed by never relying on local state
  // surviving the navigation: the target VM's id travels in the URL (`vmId` query param, appended
  // automatically by Route.path() since it isn't one of the route's own `:segments`) and is looked
  // up from the Redux-cached VM list below, which does survive the remount.
  const cloneRoute = host
    ? routeManager.routes.openstack.cloneVmFromHostDetails
    : routeManager.routes.openstack.cloneVm
  const buildClonePath = useCallback(
    (vmId: string) =>
      host
        ? cloneRoute.path({ id: host.id, tab: IHostDetailsPageTabs.VirtualMachines, vmId })
        : cloneRoute.path({ vmId }),
    [cloneRoute, host],
  )
  const cloneVmIdFromUrl = new URLSearchParams(location.search).get('vmId')
  const virtualMachinesById = useSelector(virtualMachinesByIdSelector)
  const [selectedVms, setSelectedVms] = useState<SelectorModel[]>(null)
  const [showDeleteDialog, setShowDeleteDialog] = useState<boolean>(false)
  const [showPowerDialog, setShowPowerDialog] = useState<boolean>(false)
  const [showRenameDialog, setShowRenameDialog] = useState<boolean>(false)
  const [showSnapshotDialog, setShowSnapshotDialog] = useState<boolean>(false)
  const [showRebuildDialog, setShowRebuildDialog] = useState<boolean>(false)
  const [showAddFixedIpModal, setShowAddFixedIpModal] = useState<boolean>(false)
  const [showRemoveFixedIpDialog, setShowRemoveFixedIpDialog] = useState<boolean>(false)
  const [showAddFloatingIpDialog, setShowAddFloatingIpDialog] = useState<boolean>(false)
  const [showAddServerGroupModal, setShowAddServerGroupModal] = useState<boolean>(false)
  const [showRemoveServerGroupModal, setShowRemoveServerGroupModal] = useState<boolean>(false)
  const [showRemoveFloatingIpDialog, setShowRemoveFloatingIpDialog] = useState<boolean>(false)
  const [showEditVmSecurityGroupsModal, setShowEditVmSecurityGroupsModal] = useState<boolean>(false)
  const [showEditVmMetadataModal, setShowEditVmMetadataModal] = useState<boolean>(false)
  const [showConfirmResizeDialog, setShowConfirmResizeDialog] = useState<boolean>(false)
  const [showResizeDialog, setShowResizeDialog] = useState<boolean>(false)
  const [showMigrateDialog, setShowMigrateDialog] = useState<boolean>(false)
  const [showChangeTenantDialog, setShowChangeTenantDialog] = useState<boolean>(false)
  const [showManageVolumesModal, setShowManageVolumesModal] = useState<boolean>(false)
  const [showRescueDialog, setShowRescueDialog] = useState<boolean>(false)
  const [showUnrescueDialog, setShowUnrescueDialog] = useState<boolean>(false)
  const [showHotplugDialog, setShowHotplugDialog] = useState<boolean>(false)
  const [showMountISODialog, setShowMountISODialog] = useState<boolean>(false)
  const [showUnmountISODialog, setShowUnmountISODialog] = useState<boolean>(false)
  const [showCustomLeaseModal, setShowCustomLeaseModal] = useState<boolean>(false)
  const [showMigrationPriorityDialog, setShowMigrationPriorityDialog] = useState<boolean>(false)
  const [showAssignFdDialog, setShowAssignFdDialog] = useState<boolean>(false)
  const [showRemoveFdDialog, setShowRemoveFdDialog] = useState<boolean>(false)
  const [showManageVolumeTerminationModal, setShowManageVolumeTerminationModal] =
    useState<boolean>(false)
  const [selectedPowerAction, setSelectedPowerAction] = useState<string>('')
  const [rebootType, setRebootType] = useState('SOFT')
  const [errorVm, setErrorVm] = useState<string>(null)
  const [migrationFailureVm, setMigrationFailureVm] = useState(null) // type is some subset of VM selector
  const [consoleState, setConsoleState] = useState({
    open: false,
    loading: false,
    error: null as string | null,
    vmName: '',
  })
  const [showEditVmPortSecurityModal, setShowEditVmPortSecurityModal] = useState<boolean>(false)
  const [blueprint, setBlueprint] = useState(null)
  const [isoMountStateByVmId, setIsoMountStateByVmId] = useState<
    Record<string, { mounted: boolean; volumeId?: string; volumeName?: string }>
  >({})

  const session = useSelector<RootState, SessionState>(prop(sessionStoreKey))
  const chameleonInfo = useSelector<RootState, ChameleonReducer>(prop(chameleonKey))
  const userRole = session?.userDetails?.role || ''
  const { dispatch } = store

  // Initialize from session (persisted across page navigations, shared with VirtualMachinesListPage)
  const [allTenants, setAllTenants] = useState(session?.vmsAllTenants ?? false)
  const [allOwners, setAllOwners] = useState(session?.vmsAllOwners ?? false)

  const manageColumnsToggleRef = useRef<(() => void) | null>(null)
  const isHostDetailsPage = host !== null

  const {
    // Data
    paginatedVMs,
    totalCount,

    // Loading states
    loading: activeLoading,
    polling: activePolling,
    message: activeMessage,

    // Pagination
    pageMarkers,
    currentPage,
    pageSize,
    hasNextPage,
    isPaginationLoading,
    handlePageSizeChange,

    // Actions
    reload: activeReload,
    poll: activePoll,
    handlePageChange,
    handleRefresh,

    // Search
    searchParams,
    handleSearch,
    handleClearSearch,
    filterParamsForUI,

    // Selection management
    clearSelectionRef,
    clearSelection,

    // Sort
    sortKey,
    sortDir,
    handleSort,
  } = useVirtualMachinesPagination({
    host: isHostDetailsPage ? host?.id : undefined,
    pageSize: session?.vmsPageSize || 10,
    allTenants: session?.vmsAllTenants || false,
    allOwners: session?.vmsAllOwners || false,
    sessionKey: isHostDetailsPage ? 'hostList' : 'vmList',
    sessionSearchParams: isHostDetailsPage ? 'hostVmsSearchParams' : 'vmsSearchParams',
    sessionSortKey: isHostDetailsPage ? 'hostVmsSortKey' : 'vmsSortKey',
    sessionSortDir: isHostDetailsPage ? 'hostVmsSortDir' : 'vmsSortDir',
    sessionPageSize: isHostDetailsPage ? 'hostVmsPageSize' : 'vmsPageSize',
    sessionVMCachedParams: isHostDetailsPage ? 'hostVmsCachedParams' : 'vmsCachedParams',
    sessionAllVMCachedParams: isHostDetailsPage ? 'hostAllVmsCachedParams' : 'vmsAllCachedParams',
    sessionSelfVMCachedParams: isHostDetailsPage
      ? 'hostSelfVmsCachedParams'
      : 'vmsSelfCachedParams',
    defaultSearchParams: defaultVMFilterParams,
  })

  // C66208 bug fix: `virtualMachinesByIdSelector` only ever reads `DataKeys.OpenstackVirtualMachines`
  // (see selectors.ts), but `useVirtualMachinesPagination` fetches into a *different* DataKey —
  // `SelfOpenstackVirtualMachines` (self-service/member users) or `AllOpenstackVirtualMachines`
  // (all-tenants view) — depending on user context (`selectByContext` inside that hook). For such
  // a user, a VM that's definitely visible in the grid (it's right there in `paginatedVMs`) could
  // still be entirely absent from the by-id cache, which reads the wrong key — not a timing race,
  // a cache-key mismatch. Checking `paginatedVMs` first (the exact data this component already
  // rendered, guaranteed consistent with whatever the user just clicked) fixes that; the by-id
  // cache remains a fallback for completeness, unchanged from before.
  const cloneTargetVm = cloneVmIdFromUrl
    ? ((paginatedVMs as SelectorModel[])?.find((item) => item.id === cloneVmIdFromUrl) ??
      virtualMachinesById[cloneVmIdFromUrl])
    : null

  const { prefs } = useScopedPreferences()
  const { currentTenant } = prefs

  const onRefresh = useCallback(() => {
    refreshVmDependencies(allTenants)
    handleRefresh()
  }, [allTenants, handleRefresh])

  useEffect(() => {
    const handleIsoOperationCompleted = (event: Event) => {
      const details = (event as CustomEvent<{ vmId?: string; operation?: 'mount' | 'unmount' }>)
        ?.detail
      if (details?.vmId && details?.operation) {
        setIsoMountStateByVmId((prev) => {
          const prevState = prev?.[details.vmId]
          const mounted = details.operation === 'mount'
          if (prevState?.mounted === mounted) {
            return prev
          }
          return {
            ...prev,
            [details.vmId]: {
              ...prevState,
              mounted,
            },
          }
        })
      }
      onRefresh()
    }

    window.addEventListener(VM_ISO_OPERATION_COMPLETED_EVENT, handleIsoOperationCompleted)
    return () => {
      window.removeEventListener(VM_ISO_OPERATION_COMPLETED_EVENT, handleIsoOperationCompleted)
    }
  }, [onRefresh])

  useEffect(() => {
    const queryBlueprint = async () => {
      try {
        const blueprints = await getBlueprints()
        const blueprint = blueprints?.[0]
        setBlueprint(blueprint)
      } catch {
        setBlueprint(null)
      }
    }

    queryBlueprint()
  }, [])

  useEffect(() => {
    if (paginatedVMs?.length > 0 && chameleonInfo?.createdVmId) {
      chameleonVmSurveyCheck(paginatedVMs, chameleonInfo?.createdVmId, dispatch)
    }
  }, [paginatedVMs, chameleonInfo?.createdVmId])

  useEffect(() => {
    if (paginatedVMs?.length > 0 && chameleonInfo?.migratedVmIds?.length > 0) {
      chameleonVmMigrationSurveyCheck(paginatedVMs, chameleonInfo?.migratedVmIds, dispatch)
    }
  }, [paginatedVMs, chameleonInfo?.migratedVmIds])

  // Cleanup effect: Clear host-specific session cache params when component unmounts
  useEffect(() => {
    const dependenciesNotLoaded = anyVmDependencyNotLoaded(allTenants)
    if (dependenciesNotLoaded) {
      refreshVmDependencies(allTenants)
    }
    return () => {
      // Only clear host-specific session cache params if we're on the host details page
      if (!isHostDetailsPage) {
        return
      }

      dispatch(
        sessionActions.updateSession({
          // hostVmsSearchParams: null,
          hostVmsSortKey: undefined,
          hostVmsSortDir: undefined,
          hostVmsCachedParams: undefined,
          hostAllVmsCachedParams: undefined,
          hostSelfVmsCachedParams: undefined,
        }),
      )
    }
  }, [allTenants])

  // Handle allTenants toggle with session persistence (shared with VirtualMachinesListPage)
  const handleAllTenantsToggle = useCallback(() => {
    const newValue = !allTenants
    setAllTenants(newValue)
    setSelectedVms(null)
    clearSelection()
    dispatch(sessionActions.updateSession({ vmsAllTenants: newValue }))
  }, [allTenants, dispatch, clearSelection])

  // Handle allOwners toggle with session persistence (shared with VirtualMachinesListPage)
  const handleAllOwnersToggle = useCallback(() => {
    const newValue = !allOwners
    setAllOwners(newValue)
    setSelectedVms(null)
    clearSelection()
    dispatch(sessionActions.updateSession({ vmsAllOwners: newValue }))
  }, [allOwners, dispatch, clearSelection])

  useEffect(() => {
    getNovaVersion()
  }, [])

  useListAction(listResmgrHosts)
  const hosts = useSelector(resmgrHostsSelector)

  // FR-039: Load cluster HA status data for the per-VM HA Protection Status column.
  const { loading: clusterHostGroupsLoading } = useListAction(listClusterHostGroups)
  const clusterHostGroups = useSelector(clusterHostGroupsSelector)
  const { reload: loadClusterVMHAStatus } = useListAction(listClusterVMHAStatus, {
    loadOnDemand: true,
    params: { clusters: clusterHostGroups },
  })
  const vmhaStatusFetchedRef = useRef(false)
  useEffect(() => {
    if (vmhaStatusFetchedRef.current || !clusterHostGroups?.length || clusterHostGroupsLoading) {
      return
    }
    vmhaStatusFetchedRef.current = true
    loadClusterVMHAStatus(true, true)
  }, [clusterHostGroups, clusterHostGroupsLoading, loadClusterVMHAStatus])
  // The delete guards live inside memoized batch-action / row-menu specs whose
  // dependency arrays do not (and cannot cheaply) include `hosts`, so a plain
  // `hosts` capture would freeze at its first-render value (usually [] before
  // the resmgr call resolves) and the guard would read stale host liveness. A
  // ref that always points at the latest `hosts` lets those frozen closures read
  // the current list, keeping the delete gate in sync with the "(host offline)"
  // badge without recreating the specs on every poll.
  const hostsRef = useRef(hosts)
  hostsRef.current = hosts

  useListAction(listMigrations)
  const { update: pollMigrations, updating: pollingMigrations } =
    useUpdateAction(getMigrationsSince)

  const dropdownBatchActions: GridDropdownBatchActionGroup<SelectorModel>[] = useMemo(
    () => [
      {
        // icon: 'power-off',
        label: 'Power Actions',
        actions: [
          {
            label: 'Start',
            icon: 'circle-play',
            cond: (selected) => {
              return selected.every((vm) => ['shutoff', 'stopped'].includes(vm?.state))
            },
            disabledTooltip: (selected) =>
              selected.every((vm) => !['shutoff', 'stopped'].includes(vm?.state))
                ? ACTION_LIMITATION_TOOLTIPS.START
                : '',
            BatchActionButton: getGridDialogButton(
              PowerVirtualMachineDialog,
              { action: 'start' },
              { className: classes.dropdownAction },
            ),
            permissionsPath: [
              'nova',
              'os_compute_api.servers.start:POST:/servers/{server_id}/action',
            ],
          },
          {
            label: 'Stop',
            icon: 'stop',
            cond: (selected) => {
              return selected.every((vm) => vm?.state === 'active')
            },
            disabledTooltip: (selected) =>
              selected.every((vm) => vm?.state !== 'active') ? ACTION_LIMITATION_TOOLTIPS.STOP : '',
            BatchActionButton: getGridDialogButton(
              PowerVirtualMachineDialog,
              { action: 'stop' },
              { className: classes.dropdownAction },
            ),
            permissionsPath: [
              'nova',
              'os_compute_api.servers.stop:POST:/servers/{server_id}/action',
            ],
          },
          {
            label: 'Reboot',
            icon: 'repeat',
            cond: (selected) => {
              return selected.some((vm) => canSoftReboot(vm))
            },
            disabledTooltip: ACTION_LIMITATION_TOOLTIPS.REBOOT,
            BatchActionButton: getGridDialogButton(
              PowerVirtualMachineDialog,
              { action: 'reboot' },
              { className: classes.dropdownAction },
            ),
            permissionsPath: [
              'nova',
              'os_compute_api.servers.reboot:POST:/servers/{server_id}/action',
            ],
          },
          {
            label: 'Hard Reboot',
            icon: 'rotate-exclamation',
            cond: (selected) => {
              return selected.some((vm) => canHardReboot(vm))
            },
            disabledTooltip: ACTION_LIMITATION_TOOLTIPS.HARD_REBOOT,
            BatchActionButton: getGridDialogButton(
              PowerVirtualMachineDialog,
              { action: 'reboot', type: 'HARD' },
              { className: classes.dropdownAction },
            ),
            permissionsPath: [
              'nova',
              'os_compute_api.servers.reboot:POST:/servers/{server_id}/action',
            ],
          },
          {
            label: 'Pause',
            icon: 'circle-pause',
            cond: (selected) => {
              return selected.some((vm) => vm?.state === 'active')
            },
            disabledTooltip: (selected) =>
              selected.every((vm) => vm?.state !== 'active')
                ? ACTION_LIMITATION_TOOLTIPS.PAUSE
                : '',
            BatchActionButton: getGridDialogButton(
              PowerVirtualMachineDialog,
              { action: 'pause' },
              { className: classes.dropdownAction },
            ),
            permissionsPath: [
              'nova',
              'os_compute_api.os_pause_server.pause:POST:/servers/{server_id}/action',
            ],
          },
          {
            label: 'Unpause',
            icon: 'circle-play',
            cond: (selected) => {
              return selected.some((vm) => vm?.state === 'paused')
            },
            disabledTooltip: (selected) =>
              selected.every((vm) => vm?.state !== 'paused')
                ? ACTION_LIMITATION_TOOLTIPS.UNPAUSE
                : '',
            BatchActionButton: getGridDialogButton(
              PowerVirtualMachineDialog,
              { action: 'unpause' },
              { className: classes.dropdownAction },
            ),
            permissionsPath: [
              'nova',
              'os_compute_api.os_pause_server.unpause:POST:/servers/{server_id}/action',
            ],
          },
          {
            label: 'Suspend',
            icon: 'pause',
            cond: (selected) => {
              return selected.some((vm) => vm?.state === 'active')
            },
            disabledTooltip: (selected) =>
              selected.every((vm) => vm?.state !== 'active')
                ? ACTION_LIMITATION_TOOLTIPS.SUSPEND
                : '',
            BatchActionButton: getGridDialogButton(
              PowerVirtualMachineDialog,
              { action: 'suspend' },
              { className: classes.dropdownAction },
            ),
            permissionsPath: [
              'nova',
              'os_compute_api.os_suspend_server.suspend:POST:/servers/{server_id}/action',
            ],
          },
          {
            label: 'Resume',
            icon: 'play',
            cond: (selected) => {
              return selected.some((vm) => vm?.state === 'suspended')
            },
            disabledTooltip: (selected) =>
              selected.every((vm) => vm?.state !== 'suspended')
                ? ACTION_LIMITATION_TOOLTIPS.RESUME
                : '',
            BatchActionButton: getGridDialogButton(
              PowerVirtualMachineDialog,
              { action: 'resume' },
              { className: classes.dropdownAction },
            ),
            permissionsPath: [
              'nova',
              'os_compute_api.os_suspend_server.resume:POST:/servers/{server_id}/action',
            ],
          },
          {
            label: 'Rescue',
            icon: 'laptop-medical',
            cond: (selected) => selected.length === 1 && selected[0]?.state !== 'rescued',
            disabledTooltip: (selected) =>
              selected.length > 1
                ? ACTION_LIMITATION_TOOLTIPS.SINGLE_VM_ONLY
                : selected[0]?.state === 'rescued'
                  ? ACTION_LIMITATION_TOOLTIPS.ALREADY_RESCUED
                  : '',
            BatchActionButton: getGridDialogButton(RescueVmDialog, null, {
              className: classes.dropdownAction,
            }),
            permissionsPath: ['nova', 'os_compute_api.os_rescue:POST:/servers/{server_id}/action'],
          },
          {
            label: 'Unrescue',
            icon: 'laptop-binary',
            cond: (selected) => selected.length === 1 && selected[0]?.state === 'rescued',
            disabledTooltip: (selected) =>
              selected.length > 1
                ? ACTION_LIMITATION_TOOLTIPS.SINGLE_VM_ONLY
                : selected[0]?.state !== 'rescued'
                  ? ACTION_LIMITATION_TOOLTIPS.ALREADY_NOT_RESCUED
                  : '',
            BatchActionButton: getGridDialogButton(UnrescueVmDialog, null, {
              className: classes.dropdownAction,
            }),
            permissionsPath: [
              'nova',
              'os_compute_api.os_unrescue:POST:/servers/{server_id}/action',
            ],
          },
        ],
      },
      {
        label: 'Networking',
        actions: [
          {
            label: 'Add Network Interface',
            icon: 'square-plus',
            cond: (selected) => selected.length === 1,
            disabledTooltip: ACTION_LIMITATION_TOOLTIPS.ADD_NETWORK_INTERFACE,
            BatchActionButton: getGridDialogButton(AddFixedIpModal, null, {
              className: classes.dropdownAction,
            }),
            permissionsPath: [
              'nova',
              'os_compute_api.os_attach_interfaces.create:POST:/servers/{server_id}/os-interface',
            ],
          },
          {
            label: 'Remove Network Interface',
            icon: 'square-minus',
            cond: (selected) => selected.length === 1,
            disabledTooltip: ACTION_LIMITATION_TOOLTIPS.REMOVE_NETWORK_INTERFACE,
            BatchActionButton: getGridDialogButton(RemoveFixedIpDialog, null, {
              className: classes.dropdownAction,
            }),
            permissionsPath: [
              'nova',
              'os_compute_api.os_attach_interfaces.delete:DELETE:/servers/{server_id}/os-interface/{port_id}',
            ],
          },
          {
            label: 'Add Public IP',
            icon: 'square-plus',
            cond: (selected) => selected.length === 1 && !isVMUsingOnlySimpleNetwork(selected[0]),
            disabledTooltip: (selected) =>
              selected.length > 1
                ? ACTION_LIMITATION_TOOLTIPS.ADD_PUBLIC_IP
                : isVMUsingOnlySimpleNetwork(selected[0])
                  ? ACTION_LIMITATION_TOOLTIPS.ADD_PUBLIC_IP_L2_NETWORK
                  : '',
            BatchActionButton: getGridDialogButton(AddFloatingIpDialog, null, {
              className: classes.dropdownAction,
            }),
            permissionsPath: [
              'nova',
              'os_compute_api.os_floating_ips.create:POST:/os-floating-ips',
            ],
          },
          {
            label: 'Remove Public IP',
            icon: 'square-minus',
            cond: (selected) => selected.length === 1 && !isVMUsingOnlySimpleNetwork(selected[0]),
            disabledTooltip: (selected) =>
              selected.length > 1
                ? ACTION_LIMITATION_TOOLTIPS.REMOVE_PUBLIC_IP
                : isVMUsingOnlySimpleNetwork(selected[0])
                  ? ACTION_LIMITATION_TOOLTIPS.REMOVE_PUBLIC_IP_L2_NETWORK
                  : '',
            BatchActionButton: getGridDialogButton(RemoveFloatingIpDialog, null, {
              className: classes.dropdownAction,
            }),
            permissionsPath: [
              'nova',
              'os_compute_api.os_floating_ips.delete:DELETE:/os-floating-ips/{floating_ip_id}',
            ],
          },
          {
            label: 'Edit Security Groups',
            icon: 'lock',
            cond: (selected) => selected.length === 1 && !isVMUsingOnlySimpleNetwork(selected[0]),
            disabledTooltip: (selected) =>
              selected.length > 1
                ? ACTION_LIMITATION_TOOLTIPS.EDIT_SECURITY_GROUPS
                : isVMUsingOnlySimpleNetwork(selected[0])
                  ? ACTION_LIMITATION_TOOLTIPS.EDIT_SECURITY_GROUPS_L2_NETWORK
                  : '',
            BatchActionButton: getGridDialogButton(EditVmSecurityGroupsModal, null, {
              className: classes.dropdownAction,
            }),
            permissionsPath: ['neutron', 'update_port:PUT:/ports/{id}'],
          },
          {
            label: 'Edit Port Security',
            icon: 'shield-alt',
            cond: (selected) => selected.length === 1 && !isVMUsingOnlySimpleNetwork(selected[0]),
            disabledTooltip: (selected) =>
              selected.length > 1
                ? ACTION_LIMITATION_TOOLTIPS.EDIT_PORT_SECURITY
                : isVMUsingOnlySimpleNetwork(selected[0])
                  ? ACTION_LIMITATION_TOOLTIPS.EDIT_PORT_SECURITY_L2_NETWORK
                  : '',
            BatchActionButton: getGridDialogButton(EditVmPortSecurityModal, null, {
              className: classes.dropdownAction,
            }),
            permissionsPath: ['neutron', 'update_port.port_security_enabled:PUT:/ports/{id}'],
          },
        ],
      },
      {
        // icon: 'square-ellipsis',
        label: 'Other',
        actions: [
          {
            label: 'Rename',
            icon: 'edit',
            cond: (selected) => selected.length === 1,
            disabledTooltip: ACTION_LIMITATION_TOOLTIPS.RENAME_VM,
            BatchActionButton: getGridDialogButton(RenameVirtualMachineDialog, null, {
              className: classes.dropdownAction,
            }),
            permissionsPath: ['nova', 'os_compute_api.servers.update:PUT:/servers/{server_id}'],
          },
          {
            label: 'Change Owner',
            icon: 'user-edit',
            cond: (selected) => userRole !== 'member' && selected?.length === 1,
            disabledTooltip: (selected) =>
              selected?.length > 1
                ? ACTION_LIMITATION_TOOLTIPS.CHANGE_OWNER_VM
                : !isAdmin()
                  ? ACTION_LIMITATION_TOOLTIPS.ADMIN_ONLY
                  : '',
            BatchActionButton: getGridDialogButton(ChangeOwnerDialog, null, {
              className: classes.dropdownAction,
            }),
            permissionsPath: [
              'nova',
              'os_compute_api.servers.change_owner:POST:/servers/{server_id}/action',
            ],
          },
          {
            label: 'Change Tenant',
            icon: 'arrow-right-arrow-left',
            cond: (selected) => canChangeTenantVms(userRole, selected),
            disabledTooltip: (selected) =>
              !isAdmin()
                ? ACTION_LIMITATION_TOOLTIPS.ADMIN_ONLY
                : ACTION_LIMITATION_TOOLTIPS.CHANGE_TENANT_VM_STATE,
            BatchActionButton: getGridDialogButton(ChangeTenantDialog, null, {
              className: classes.dropdownAction,
            }),
            permissionsPath: [
              'nova',
              'os_compute_api.servers.change_owner:POST:/servers/{server_id}/action',
            ],
          },
          {
            label: 'Snapshot',
            icon: 'camera',
            cond: (selected) =>
              selected.length === 1 &&
              !selected?.some((vm) => vm?.flavor?.extra_specs?.['pci_passthrough:alias']),
            disabledTooltip: (selected) =>
              selected.length > 1
                ? ACTION_LIMITATION_TOOLTIPS.SNAPSHOT_VM
                : selected?.every((vm) => vm?.flavor?.extra_specs?.['pci_passthrough:alias'])
                  ? TOOLTIP_MESSAGES.GPU_PASSTHROUGH_SNAPSHOT
                  : '',
            BatchActionButton: getGridDialogButton(SnapshotVirtualMachineDialog, null, {
              className: classes.dropdownAction,
            }),
            permissionsPath: [
              'nova',
              'os_compute_api.servers.create_image:POST:/servers/{server_id}/action',
            ],
          },
          {
            label: 'Clone',
            icon: 'clone',
            cond: (selected) => selected.length === 1,
            disabledTooltip: ACTION_LIMITATION_TOOLTIPS.CLONE_VM,
            // CLVM-FR-015: route-driven wizard, not a toggled dialog.
            BatchActionButton: getGridNavigationButton(
              (selected) => buildClonePath(selected?.[0]?.id),
              setSelectedVms,
              { className: classes.dropdownAction },
            ),
            permissionsPath: ['nova', 'os_compute_api.servers.create:POST:/servers'],
          },
          {
            label: 'Manage Volumes',
            icon: 'hard-drive',
            cond: (vms) =>
              vms.length === 1 &&
              ['active', 'suspended', 'stopped'].includes(vms[0].state) &&
              isVolumeBackedVm(vms[0]),
            disabledTooltip: (selected) =>
              selected.length > 1
                ? ACTION_LIMITATION_TOOLTIPS.SINGLE_VM_ONLY
                : !['active', 'suspended', 'stopped'].includes(selected[0]?.state)
                  ? ACTION_LIMITATION_TOOLTIPS.MANAGE_VOLUMES_STATE
                  : !isVolumeBackedVm(selected[0])
                    ? VOLUME_BACKED_VM_ERROR_MSG
                    : '',
            BatchActionButton: getGridDialogButton(
              ManageVolumesModal,
              { isOpenModal: true },
              {
                className: classes.dropdownAction,
              },
            ),
            permissionsPath: [
              'nova',
              'os_compute_api.os_volumes_attachments.create:POST:/servers/{server_id}/os-volume_attachments',
            ],
          },
          {
            label: 'Volume Termination',
            icon: 'trash-can-plus',
            cond: (vms) => vms.length === 1 && vms?.[0]?.volumeIds?.length > 0,
            disabledTooltip: (selected) =>
              selected.length > 1
                ? ACTION_LIMITATION_TOOLTIPS.VOLUME_TERMINATION_SINGLE_VM
                : ACTION_LIMITATION_TOOLTIPS.VOLUME_TERMINATION,
            BatchActionButton: getGridDialogButton(ManageVolumeTerminationModal, null, {
              className: classes.dropdownAction,
            }),
            permissionsPath: [
              'nova',
              'os_compute_api.os_volumes_attachments.update:PUT:/servers/{server_id}/os-volume_attachments/{volume_id}',
            ],
          },
          {
            label: 'Edit Metadata',
            icon: 'tags',
            cond: (selected) => selected.length === 1 && selected[0]?.state !== 'resized',
            disabledTooltip: (selected) =>
              selected.length > 1
                ? ACTION_LIMITATION_TOOLTIPS.EDIT_METADATA
                : selected[0]?.state === 'resized'
                  ? ACTION_LIMITATION_TOOLTIPS.EDIT_METADATA_VM_RESIZED
                  : '',
            BatchActionButton: getGridDialogButton(EditVmMetadataModal, null, {
              className: classes.dropdownAction,
            }),
            permissionsPath: [
              'nova',
              'os_compute_api.server_metadata.update_all:PUT:/servers/{server_id}/metadata',
            ],
          },
          {
            label: 'Resize',
            icon: 'up-right-and-down-left-from-center',
            cond: canResizeVms,
            disabledTooltip: getResizeVmsTooltip,
            BatchActionButton: getGridDialogButton(ResizeVirtualMachineDialog, null, {
              className: classes.dropdownAction,
            }),
            permissionsPath: [
              'nova',
              'os_compute_api.servers.resize:POST:/servers/{server_id}/action',
            ],
          },
          {
            label: 'Confirm Resize',
            icon: 'box-check',
            cond: canConfirmResizeVms,
            disabledTooltip: getConfirmResizeVmsTooltip,
            BatchActionButton: getGridDialogButton(ConfirmResizeDialog, null, {
              className: classes.dropdownAction,
            }),
            permissionsPath: [
              'nova',
              'os_compute_api.servers.confirm_resize:POST:/servers/{server_id}/action',
            ],
          },
          {
            label: 'Hotplug',
            icon: 'plug-circle-plus',
            cond: canHotplugVms,
            disabledTooltip: getHotplugVmsTooltip,
            BatchActionButton: getGridDialogButton(HotplugVmDialog, null, {
              className: classes.dropdownAction,
            }),
            permissionsPath: ['nova', 'os_compute_api.servers.update:PUT:/servers/{server_id}'],
          },
          {
            label: 'Migrate',
            icon: 'database',
            cond: (selected) => canMigrateVMs(userRole, selected),
            hideCond: (selected) => !isAdmin(),
            disabledTooltip: (selected) => <GetDisabledTooltipForVMs selected={selected} />,
            BatchActionButton: getGridDialogButton(MigrateVirtualMachineDialog, null, {
              className: classes.dropdownAction,
            }),
            permissionsPath: [
              'nova',
              'os_compute_api.os_migrate_server.migrate:POST:/servers/{server_id}/action',
            ],
          },
          {
            label: 'Migration Priority',
            icon: 'ranking-star',
            cond: (selected) => selected.length === 1 && isAdmin(),
            hideCond: (selected) => !isAdmin(),
            disabledTooltip: (selected) =>
              selected.length > 1
                ? ACTION_LIMITATION_TOOLTIPS.UPDATE_MIGRATION_PRIORITY
                : !isAdmin()
                  ? ACTION_LIMITATION_TOOLTIPS.MIGRATION_PRIORITY_ADMIN_ONLY
                  : '',
            BatchActionButton: getGridDialogButton(UpdateMigrationPriorityDialog, null, {
              className: classes.dropdownAction,
            }),
            permissionsPath: [
              'nova',
              'os_compute_api.os_migrate_server.migrate:POST:/servers/{server_id}/action',
            ],
          },
          {
            label: 'Assign Failure Domain',
            icon: 'shield-halved',
            cond: (selected) => selected?.every((item) => !item?.stretchedClusterName),
            hideCond: () => !isAdmin(),
            disabledTooltip: 'The selected VM(s) already have a failure domain assigned',
            hideIfDisabled: true,
            BatchActionButton: getGridDialogButton(AssignStretchedClusterDialog, null, {
              className: classes.dropdownAction,
            }),
            permissionsPath: [
              'nova',
              'os_compute_api.server_metadata.update_all:PUT:/servers/{server_id}/metadata',
            ],
          },
          {
            label: 'Change Failure Domain',
            icon: 'shield-xmark',
            cond: (selected) => selected?.every((item) => !!item?.stretchedClusterName),
            hideCond: () => !isAdmin(),
            disabledTooltip: 'The selected VM(s) do not have a failure domain assigned',
            hideIfDisabled: true,
            BatchActionButton: getGridDialogButton(RemoveStretchedClusterDialog, null, {
              className: classes.dropdownAction,
            }),
            permissionsPath: [
              'nova',
              'os_compute_api.server_metadata.update_all:PUT:/servers/{server_id}/metadata',
            ],
          },
          {
            label: 'Rebuild',
            icon: 'screwdriver-wrench',
            cond: (selected) => selected.length === 1,
            disabledTooltip: ACTION_LIMITATION_TOOLTIPS.REBUILD_VM,
            BatchActionButton: getGridDialogButton(RebuildVirtualMachineDialog, null, {
              className: classes.dropdownAction,
            }),
            permissionsPath: [
              'nova',
              'os_compute_api.servers.rebuild:POST:/servers/{server_id}/action',
            ],
          },
          {
            label: 'Customize Lease',
            icon: 'key',
            cond: (selected) =>
              selected.length === 1 &&
              selected[0]?.tenant_id === currentTenant &&
              !!selected[0]?.leasePolicy,
            disabledTooltip: (selected) =>
              selected.length > 1
                ? ACTION_LIMITATION_TOOLTIPS.CUSTOMIZE_LEASE
                : selected[0]?.tenant_id !== currentTenant
                  ? ACTION_LIMITATION_TOOLTIPS.VM_NOT_OWNED
                  : !selected[0]?.leasePolicy
                    ? ACTION_LIMITATION_TOOLTIPS.VM_NOT_LEASED
                    : '',
            keepRowsSelected: false,
            // handleAction: (vms) => {
            //   setSelectedVms(vms)
            //   setShowCustomLeaseModal(true)
            //   return true // returned true to remove selection after action. this works even without returning true but just to be explicit
            // },
            BatchActionButton: getGridDialogButton(CustomVmLeaseModal, null, {
              className: classes.dropdownAction,
            }),
            // TODO: There is no permission for lease, pick something
            // that makes sense for now
            permissionsPath: ['nova', 'os_compute_api.servers.delete:DELETE:/servers/{server_id}'],
          },
          {
            label: 'Mount ISO',
            icon: 'mount-iso',
            cond: (selected) => {
              const vm = selected[0]
              return selected.length === 1 && canIsoMount(vm)
            },
            disabledTooltip: (selected) => {
              const vm = selected?.[0]
              if (selected.length > 1) {
                return ACTION_LIMITATION_TOOLTIPS.SINGLE_VM_ONLY
              }
              return disableMountIsoTooltip(vm)
            },
            BatchActionButton: getGridDialogButton(MountISODialog, null, {
              className: classes.dropdownAction,
            }),
            permissionsPath: ['cinder', 'volume.create_from_image:POST:/volumes'],
          },
          {
            label: 'Unmount ISO',
            icon: 'unmount-iso',
            cond: (selected) => {
              const vm = selected?.[0]

              return selected.length === 1 && canUnMountIso(vm)
            },
            disabledTooltip: (selected) => {
              const vm = selected?.[0]

              if (selected.length > 1) {
                return ACTION_LIMITATION_TOOLTIPS.SINGLE_VM_ONLY
              }
              return disabledUnMountIsoTooltip(vm)
            },
            BatchActionButton: getGridDialogButton(UnmountISODialog, null, {
              className: classes.dropdownAction,
            }),
            permissionsPath: [
              'nova',
              'os_compute_api.os_volumes_attachments.delete:DELETE:/servers/{server_id}/os-volume_attachments/{volume_id}',
            ],
          },
          {
            label: 'Add Server Group',
            icon: 'square-plus',
            cond: (selected) =>
              selected.length === 1 &&
              selected?.every(
                (vm) => !vm?.serverGroup && VALID_SERVER_GROUP_VM_STATES.includes(vm?.state),
              ),
            disabledTooltip: (selected) =>
              selected.length > 1
                ? ACTION_LIMITATION_TOOLTIPS.ADD_SERVER_GROUP_ONE_VM
                : selected?.[0]?.serverGroup
                  ? ACTION_LIMITATION_TOOLTIPS.VM_ALREADY_IN_SERVER_GROUP
                  : !VALID_SERVER_GROUP_VM_STATES.includes(selected?.[0]?.state)
                    ? ACTION_LIMITATION_TOOLTIPS.VM_NOT_IN_ACTIVE_OR_STOPPED_STATE
                    : '',
            BatchActionButton: getGridDialogButton(AddServerGroupDialog, null, {
              className: classes.dropdownAction,
            }),
            permissionsPath: [
              'nova',
              'os_compute_api.os_server_groups.add:POST:/servers/{server_id}/action',
            ],
          },
          {
            label: 'Remove Server Group',
            icon: 'square-minus',
            cond: (selected) =>
              selected?.every(
                (vm) =>
                  Boolean(vm?.serverGroup) && VALID_SERVER_GROUP_VM_STATES.includes(vm?.state),
              ),
            disabledTooltip: (selected) =>
              selected?.some((vm) => !vm?.serverGroup)
                ? ACTION_LIMITATION_TOOLTIPS.ALL_VMS_NOT_IN_SERVER_GROUP
                : selected?.some((vm) => !VALID_SERVER_GROUP_VM_STATES.includes(vm?.state))
                  ? ACTION_LIMITATION_TOOLTIPS.ALL_VMS_NOT_IN_ACTIVE_OR_STOPPED_STATE
                  : '',
            BatchActionButton: getGridDialogButton(RemoveServerGroupDialog, null, {
              className: classes.dropdownAction,
            }),
            permissionsPath: [
              'nova',
              'os_compute_api.os_server_groups.remove:POST:/servers/{server_id}/action',
            ],
          },
        ],
      },
    ],
    [classes, userRole, currentTenant],
  )

  const batchActions: GridBatchActionSpec<SelectorModel>[] = useMemo(
    () => [
      {
        cond: (vms) => {
          return vms?.every((vm) => ['shutoff', 'stopped'].includes(vm?.state))
        },
        disabledTooltip: (vms) =>
          !vms?.every((vm) => ['shutoff', 'stopped'].includes(vm?.state))
            ? ACTION_LIMITATION_TOOLTIPS.CANNOT_START_RUNNING
            : '',
        label: 'Start',
        icon: 'circle-play',
        iconOnly: true,
        tooltip: TOOLTIP_MESSAGES.START,
        handleAction: (vms) => {
          setSelectedVms(vms)
          setSelectedPowerAction('start')
          setShowPowerDialog(true)
        },
        refreshAfterSuccess: false,
        permissionsPath: ['nova', 'os_compute_api.servers.start:POST:/servers/{server_id}/action'],
      },
      {
        cond: (vms) => {
          return vms?.every((vm) => vm?.state === 'active')
        },
        disabledTooltip: (vms) =>
          !vms?.every((vm) => vm?.state === 'active')
            ? ACTION_LIMITATION_TOOLTIPS.CANNOT_STOP_STOPPED
            : '',
        label: 'Stop',
        icon: 'stop',
        iconOnly: true,
        tooltip: TOOLTIP_MESSAGES.STOP,
        handleAction: (vms) => {
          setSelectedVms(vms)
          setSelectedPowerAction('stop')
          setShowPowerDialog(true)
        },
        refreshAfterSuccess: false,
        permissionsPath: ['nova', 'os_compute_api.servers.stop:POST:/servers/{server_id}/action'],
      },
      {
        cond: (vms) => {
          return vms?.length === 1
        },
        disabledTooltip: ACTION_LIMITATION_TOOLTIPS.CONSOLE_VM,
        label: 'Console',
        icon: 'terminal',
        iconOnly: true,
        tooltip: TOOLTIP_MESSAGES.CONSOLE,
        handleAction: async ([vm]) => {
          setConsoleState({
            open: true,
            loading: true,
            error: null,
            vmName: vm?.name || 'VM',
          })

          try {
            const url = await getConsoleEndpoint({ id: vm.id })
            const withVmName = `${url}&instance=${vm?.name || vm?.id}`
            const newWindow = openConsoleWindow(withVmName)

            if (newWindow) {
              setConsoleState((prev) => ({
                ...prev,
                loading: false,
                error: null,
                open: false,
              }))
            } else {
              setConsoleState((prev) => ({
                ...prev,
                loading: false,
                error: 'Failed to open console window. Please check your popup blocker settings.',
              }))
            }
          } catch (error) {
            const { message } = generateApiErrorPayload(error)
            setConsoleState((prev) => ({
              ...prev,
              loading: false,
              error: message || 'Failed to open VM console',
            }))
          }
        },
        refreshAfterSuccess: false,
        permissionsPath: [
          'nova',
          'os_compute_api.os_remote_consoles:POST:/servers/{server_id}/action',
        ],
      },
      // {
      //   cond: (vm) => {
      //     return vm?.length >= 1
      //   },
      //   label: 'Delete VM',
      //   icon: 'trash-alt',
      //   iconOnly: true,
      //   handleAction: (vms) => {
      //     console.log(vms, 'vms')
      //     setSelectedVms(vms)
      //     setShowDeleteDialog(true)
      //   },
      //   refreshAfterSuccess: false,
      // },
    ],
    [setSelectedVms, setSelectedPowerAction, setShowPowerDialog],
  )

  // Adding this in later when I figure out bug
  const rowMenuItems: Array<GridRowMenuItemSpec<SelectorModel>> = useMemo(
    () => [
      {
        label: 'Power Actions',
        items: [
          {
            label: 'Start',
            icon: 'circle-play',
            handleClick: (vm) => {
              setSelectedVms([vm])
              setSelectedPowerAction('start')
              setShowPowerDialog(true)
            },
            cond: (selected) => {
              return ['shutoff', 'stopped'].includes(selected?.state)
            },
            disabledTooltip: 'Cannot start a running VM',
            refreshAfterSuccess: false,
            hideIfDisabled: false,
            permissionsPath: [
              'nova',
              'os_compute_api.servers.start:POST:/servers/{server_id}/action',
            ],
          },
          {
            label: 'Stop',
            icon: 'stop',
            handleClick: (vm) => {
              setSelectedVms([vm])
              setSelectedPowerAction('stop')
              setShowPowerDialog(true)
            },
            cond: (selected) => {
              return selected?.state === 'active'
            },
            disabledTooltip: 'Cannot stop a stopped VM',
            refreshAfterSuccess: false,
            hideIfDisabled: false,
            permissionsPath: [
              'nova',
              'os_compute_api.servers.stop:POST:/servers/{server_id}/action',
            ],
          },
          {
            label: 'Reboot',
            icon: 'repeat',
            handleClick: (vm) => {
              setSelectedVms([vm])
              setSelectedPowerAction('reboot')
              setRebootType('SOFT')
              setShowPowerDialog(true)
            },
            cond: (selected) => {
              return canSoftReboot(selected)
            },
            disabledTooltip: ACTION_LIMITATION_TOOLTIPS.REBOOT,
            refreshAfterSuccess: false,
            hideIfDisabled: false,
            permissionsPath: [
              'nova',
              'os_compute_api.servers.reboot:POST:/servers/{server_id}/action',
            ],
          },
          {
            label: 'Hard Reboot',
            icon: 'rotate-exclamation',
            handleClick: (vm) => {
              setSelectedVms([vm])
              setSelectedPowerAction('reboot')
              setRebootType('HARD')
              setShowPowerDialog(true)
            },
            cond: (selected) => {
              return canHardReboot(selected)
            },
            disabledTooltip: ACTION_LIMITATION_TOOLTIPS.HARD_REBOOT,
            refreshAfterSuccess: false,
            hideIfDisabled: false,
            permissionsPath: [
              'nova',
              'os_compute_api.servers.reboot:POST:/servers/{server_id}/action',
            ],
          },
          {
            label: 'Pause',
            icon: 'circle-pause',
            cond: (selected) => {
              return selected?.state === 'active'
            },
            disabledTooltip: ACTION_LIMITATION_TOOLTIPS.PAUSE,
            handleClick: (vm) => {
              setSelectedVms([vm])
              setSelectedPowerAction('pause')
              setShowPowerDialog(true)
            },
            refreshAfterSuccess: false,
            permissionsPath: [
              'nova',
              'os_compute_api.os_pause_server.pause:POST:/servers/{server_id}/action',
            ],
          },
          {
            label: 'Unpause',
            icon: 'circle-play',
            cond: (selected) => {
              return selected?.state === 'paused'
            },
            disabledTooltip: ACTION_LIMITATION_TOOLTIPS.UNPAUSE,
            handleClick: (vm) => {
              setSelectedVms([vm])
              setSelectedPowerAction('unpause')
              setShowPowerDialog(true)
            },
            refreshAfterSuccess: false,
            permissionsPath: [
              'nova',
              'os_compute_api.os_pause_server.unpause:POST:/servers/{server_id}/action',
            ],
          },
          {
            label: 'Suspend',
            icon: 'pause',
            handleClick: (vm) => {
              setSelectedVms([vm])
              setSelectedPowerAction('suspend')
              setShowPowerDialog(true)
            },
            cond: (selected) => {
              return selected?.state === 'active'
            },
            disabledTooltip: 'Cannot suspend a non-running VM',
            refreshAfterSuccess: false,
            hideIfDisabled: false,
            permissionsPath: [
              'nova',
              'os_compute_api.os_suspend_server.suspend:POST:/servers/{server_id}/action',
            ],
          },
          {
            label: 'Resume',
            icon: 'play',
            handleClick: (vm) => {
              setSelectedVms([vm])
              setSelectedPowerAction('resume')
              setShowPowerDialog(true)
            },
            cond: (selected) => {
              return selected?.state === 'suspended'
            },
            disabledTooltip: ACTION_LIMITATION_TOOLTIPS.RESUME,
            refreshAfterSuccess: false,
            hideIfDisabled: false,
            permissionsPath: [
              'nova',
              'os_compute_api.os_suspend_server.resume:POST:/servers/{server_id}/action',
            ],
          },
          {
            label: 'Rescue',
            icon: 'laptop-medical',
            cond: (selected) => {
              return selected?.state !== 'rescued'
            },
            disabledTooltip: ACTION_LIMITATION_TOOLTIPS.RESCUE,
            handleClick: (vm) => {
              setSelectedVms([vm])
              setShowRescueDialog(true)
            },
            refreshAfterSuccess: false,
            hideIfDisabled: false,
            permissionsPath: ['nova', 'os_compute_api.os_rescue:POST:/servers/{server_id}/action'],
          },
          {
            label: 'Unrescue',
            icon: 'laptop-binary',
            cond: (selected) => {
              return selected?.state === 'rescued'
            },
            disabledTooltip: ACTION_LIMITATION_TOOLTIPS.UNRESCUE,
            handleClick: (vm) => {
              setSelectedVms([vm])
              setShowUnrescueDialog(true)
            },
            refreshAfterSuccess: false,
            hideIfDisabled: false,
            permissionsPath: [
              'nova',
              'os_compute_api.os_unrescue:POST:/servers/{server_id}/action',
            ],
          },
        ],
      },
      {
        label: 'Network Actions',
        items: [
          {
            label: 'Add Network Interface',
            icon: 'square-plus',
            disabledTooltip: 'You cannot add network interface to this VM',
            handleClick: (vm) => {
              setSelectedVms([vm])
              setShowAddFixedIpModal(true)
            },
            refreshAfterSuccess: false,
            hideIfDisabled: false,
            permissionsPath: [
              'nova',
              'os_compute_api.os_attach_interfaces.create:POST:/servers/{server_id}/os-interface',
            ],
          },
          {
            label: 'Remove Network Interface',
            icon: 'square-minus',
            disabledTooltip: 'You cannot remove network interface from this VM',
            handleClick: (vm) => {
              setSelectedVms([vm])
              setShowRemoveFixedIpDialog(true)
            },
            refreshAfterSuccess: false,
            hideIfDisabled: false,
            permissionsPath: [
              'nova',
              'os_compute_api.os_attach_interfaces.delete:DELETE:/servers/{server_id}/os-interface/{port_id}',
            ],
          },
          {
            label: 'Add Public IP',
            icon: 'square-plus',
            cond: (selected) => !isVMUsingOnlySimpleNetwork(selected),
            handleClick: (vm) => {
              setSelectedVms([vm])
              setShowAddFloatingIpDialog(true)
            },
            refreshAfterSuccess: false,
            hideIfDisabled: false,
            permissionsPath: [
              'nova',
              'os_compute_api.os_floating_ips.create:POST:/os-floating-ips',
            ],
          },
          {
            label: 'Remove Public IP',
            icon: 'square-minus',
            cond: (selected) => !isVMUsingOnlySimpleNetwork(selected),
            handleClick: (vm) => {
              setSelectedVms([vm])
              setShowRemoveFloatingIpDialog(true)
            },
            refreshAfterSuccess: false,
            hideIfDisabled: false,
            permissionsPath: [
              'nova',
              'os_compute_api.os_floating_ips.delete:DELETE:/os-floating-ips/{floating_ip_id}',
            ],
          },
          {
            label: 'Edit Security Groups',
            icon: 'lock',
            cond: (selected) => !isVMUsingOnlySimpleNetwork(selected),
            disabledTooltip: 'You cannot edit security groups for this VM',
            handleClick: (vm) => {
              setSelectedVms([vm])
              setShowEditVmSecurityGroupsModal(true)
            },
            refreshAfterSuccess: false,
            hideIfDisabled: false,
            permissionsPath: ['neutron', 'update_port:PUT:/ports/{id}'],
          },
          {
            label: 'Edit Port Security',
            icon: 'shield-alt',
            cond: (selected) => !isVMUsingOnlySimpleNetwork(selected),
            disabledTooltip: 'You cannot edit port security for this VM',
            handleClick: (vm) => {
              setSelectedVms([vm])
              setShowEditVmPortSecurityModal(true)
            },
            refreshAfterSuccess: true,
            hideIfDisabled: false,
            permissionsPath: ['neutron', 'update_port.port_security_enabled:PUT:/ports/{id}'],
          },
        ],
      },
      {
        label: 'Other Actions',
        items: [
          {
            label: 'Delete',
            icon: 'trash-alt',
            cond: (vm) => canDeleteVms([vm], hostsRef.current),
            disabledTooltip: ACTION_LIMITATION_TOOLTIPS.CANNOT_DELETE_ACTIVE_VM,
            handleClick: (vm) => {
              setSelectedVms([vm])
              setShowDeleteDialog(true)
            },
            refreshAfterSuccess: false,
            hideIfDisabled: false,
            permissionsPath: ['nova', 'os_compute_api.servers.delete:DELETE:/servers/{server_id}'],
          },
          {
            label: 'Rename',
            icon: 'edit',
            handleClick: (vm) => {
              setSelectedVms([vm])
              setShowRenameDialog(true)
            },
            refreshAfterSuccess: false,
            hideIfDisabled: false,
            permissionsPath: ['nova', 'os_compute_api.servers.update:PUT:/servers/{server_id}'],
          },
          {
            label: 'Snapshot',
            icon: 'camera',
            cond: (selected) => !selected?.flavor?.extra_specs?.['pci_passthrough:alias'],
            disabledTooltip: 'Snapshot is not supported for GPU passthrough VMs',
            handleClick: (vm) => {
              setSelectedVms([vm])
              setShowSnapshotDialog(true)
            },
            refreshAfterSuccess: false,
            hideIfDisabled: false,
            permissionsPath: [
              'nova',
              'os_compute_api.servers.create_image:POST:/servers/{server_id}/action',
            ],
          },
          {
            label: 'Clone',
            icon: 'clone',
            // CLVM-FR-015: Clone VM is now a route-driven wizard, not an open-boolean dialog.
            handleClick: (vm) => {
              setSelectedVms([vm])
              history.push(buildClonePath(vm.id))
            },
            refreshAfterSuccess: false,
            hideIfDisabled: false,
            permissionsPath: ['nova', 'os_compute_api.servers.create:POST:/servers'],
          },
          {
            label: 'Manage Volumes',
            icon: 'hard-drive',
            handleClick: (vm) => {
              setSelectedVms([vm])
              setShowManageVolumesModal(true)
            },
            permissionsPath: [
              'nova',
              'os_compute_api.os_volumes_attachments.create:POST:/servers/{server_id}/os-volume_attachments',
            ],
            cond: (selected) =>
              ['active', 'suspended', 'stopped'].includes(selected?.state) &&
              isVolumeBackedVm(selected),
            disabledTooltip: (selected) =>
              !['active', 'suspended', 'stopped'].includes(selected?.state)
                ? ACTION_LIMITATION_TOOLTIPS.MANAGE_VOLUMES
                : !isVolumeBackedVm(selected)
                  ? VOLUME_BACKED_VM_ERROR_MSG
                  : '',
            refreshAfterSuccess: false,
            hideIfDisabled: false,
          },
          {
            label: 'Volume Termination',
            icon: 'trash-can-plus',
            handleClick: (vm) => {
              setSelectedVms([vm])
              setShowManageVolumeTerminationModal(true)
            },
            permissionsPath: [
              'nova',
              'os_compute_api.os_volumes_attachments.update:PUT:/servers/{server_id}/os-volume_attachments/{volume_id}',
            ],
            cond: (selected) => selected?.volumeIds?.length > 0,
            disabledTooltip: ACTION_LIMITATION_TOOLTIPS.VOLUME_TERMINATION,
            refreshAfterSuccess: false,
            hideIfDisabled: false,
          },
          {
            label: 'Edit Metadata',
            icon: 'tags',
            handleClick: (vm) => {
              setSelectedVms([vm])
              setShowEditVmMetadataModal(true)
            },
            permissionsPath: [
              'nova',
              'os_compute_api.server_metadata.update_all:PUT:/servers/{server_id}/metadata',
            ],
            disabledTooltip: ACTION_LIMITATION_TOOLTIPS.EDIT_METADATA_VM_RESIZED,
            refreshAfterSuccess: false,
            hideIfDisabled: false,
            cond: (selected) => selected?.state !== 'resized',
          },
          {
            label: 'Resize',
            icon: 'up-right-and-down-left-from-center',
            handleClick: (vm) => {
              setSelectedVms([vm])
              setShowResizeDialog(true)
            },
            permissionsPath: [
              'nova',
              'os_compute_api.servers.resize:POST:/servers/{server_id}/action',
            ],
            cond: canResizeVm,
            disabledTooltip: getResizeVmTooltip,
            refreshAfterSuccess: false,
            hideIfDisabled: false,
          },
          {
            label: 'Confirm Resize',
            icon: 'box-check',
            handleClick: (vm) => {
              setSelectedVms([vm])
              setShowConfirmResizeDialog(true)
            },
            permissionsPath: [
              'nova',
              'os_compute_api.servers.confirm_resize:POST:/servers/{server_id}/action',
            ],
            cond: canConfirmResizeVm,
            disabledTooltip: getConfirmResizeVmTooltip,
            refreshAfterSuccess: false,
            hideIfDisabled: false,
          },
          {
            label: 'Hotplug',
            icon: 'plug-circle-plus',
            handleClick: (vm) => {
              setSelectedVms([vm])
              setShowHotplugDialog(true)
            },
            permissionsPath: ['nova', 'os_compute_api.servers.update:PUT:/servers/{server_id}'],
            cond: canHotplugVm,
            disabledTooltip: (selected) =>
              !isHotplugVm(selected)
                ? ACTION_LIMITATION_TOOLTIPS.HOTPLUG_ENABLED
                : !['active', 'stopped'].includes(selected?.state)
                  ? ACTION_LIMITATION_TOOLTIPS.DISABLED_HOTPLUG_VM_STATE
                  : '',
            refreshAfterSuccess: false,
            hideIfDisabled: false,
          },
          {
            label: 'Migrate',
            icon: 'database',
            handleClick: (vm) => {
              setSelectedVms([vm])
              setShowMigrateDialog(true)
            },
            permissionsPath: [
              'nova',
              'os_compute_api.os_migrate_server.migrate:POST:/servers/{server_id}/action',
            ],
            cond: (selected) => canMigrateVMs(userRole, [selected]),
            hideCond: () => !isAdmin(),
            disabledTooltip: (selected) => <GetDisabledTooltipForVMs selected={[selected]} />,
            refreshAfterSuccess: false,
            hideIfDisabled: false,
          },
          {
            label: 'Migration Priority',
            icon: 'ranking-star',
            handleClick: (vm) => {
              setSelectedVms([vm])
              setShowMigrationPriorityDialog(true)
            },
            permissionsPath: [
              'nova',
              'os_compute_api.os_migrate_server.migrate:POST:/servers/{server_id}/action',
            ],
            cond: (selected) => isAdmin(),
            disabledTooltip: 'Only admins can update migration priority',
            hideCond: () => !isAdmin(),
            refreshAfterSuccess: false,
            hideIfDisabled: false,
          },
          {
            label: 'Change Tenant',
            icon: 'arrow-right-arrow-left',
            handleClick: (vm) => {
              setSelectedVms([vm])
              setShowChangeTenantDialog(true)
            },
            permissionsPath: [
              'nova',
              'os_compute_api.servers.change_owner:POST:/servers/{server_id}/action',
            ],
            cond: (selected) => canChangeTenantVms(userRole, [selected]),
            hideCond: () => !isAdmin(),
            disabledTooltip: ACTION_LIMITATION_TOOLTIPS.CHANGE_TENANT_VM_STATE,
            refreshAfterSuccess: false,
            hideIfDisabled: false,
          },
          {
            label: 'Assign Failure Domain',
            icon: 'shield-halved',
            handleClick: (vm) => {
              setSelectedVms([vm])
              setShowAssignFdDialog(true)
            },
            permissionsPath: [
              'nova',
              'os_compute_api.server_metadata.update_all:PUT:/servers/{server_id}/metadata',
            ],
            cond: (selected) => !selected?.stretchedClusterName,
            hideCond: () => !isAdmin(),
            disabledTooltip: 'This VM already has a failure domain assigned',
            refreshAfterSuccess: false,
            hideIfDisabled: true,
          },
          {
            label: 'Change Failure Domain',
            icon: 'shield-xmark',
            handleClick: (vm) => {
              setSelectedVms([vm])
              setShowRemoveFdDialog(true)
            },
            permissionsPath: [
              'nova',
              'os_compute_api.server_metadata.update_all:PUT:/servers/{server_id}/metadata',
            ],
            cond: (selected) => !!selected?.stretchedClusterName,
            hideCond: () => !isAdmin(),
            disabledTooltip: 'This VM does not have a failure domain assigned',
            refreshAfterSuccess: false,
            hideIfDisabled: true,
          },
          {
            label: 'Rebuild',
            icon: 'screwdriver-wrench',
            handleClick: (vm) => {
              setSelectedVms([vm])
              setShowRebuildDialog(true)
            },
            refreshAfterSuccess: false,
            hideIfDisabled: false,
            permissionsPath: [
              'nova',
              'os_compute_api.servers.rebuild:POST:/servers/{server_id}/action',
            ],
          },
          {
            label: 'Customize Lease',
            icon: 'key',
            handleClick: (vm) => {
              setSelectedVms([vm])
              setShowCustomLeaseModal(true)
            },
            cond: (selected) => selected.tenant_id === currentTenant && !!selected.leasePolicy,
            disabledTooltip: (selected) =>
              selected?.tenant_id !== currentTenant
                ? 'VM is not owned by you. Please select a VM owned by you to customize lease.'
                : !selected?.leasePolicy
                  ? 'VM is not leased. Please select a leased VM to customize lease.'
                  : '',
            refreshAfterSuccess: false,
            hideIfDisabled: false,
            permissionsPath: ['nova', 'os_compute_api.servers.delete:DELETE:/servers/{server_id}'],
          },
          {
            label: 'Add Server Group',
            icon: 'square-plus',
            cond: (selected) =>
              !selected?.serverGroup && ['active', 'stopped'].includes(selected?.state),
            disabledTooltip: (selected) =>
              selected?.serverGroup
                ? ACTION_LIMITATION_TOOLTIPS.VM_ALREADY_IN_SERVER_GROUP
                : !['active', 'stopped'].includes(selected?.state)
                  ? ACTION_LIMITATION_TOOLTIPS.VM_NOT_IN_ACTIVE_OR_STOPPED_STATE
                  : '',
            handleClick: (vm) => {
              setSelectedVms([vm])
              setShowAddServerGroupModal(true)
            },
            refreshAfterSuccess: false,
            permissionsPath: [
              'nova',
              'os_compute_api.os_server_groups.add:POST:/servers/{server_id}/action',
            ],
          },
          {
            label: 'Remove Server Group',
            icon: 'square-minus',
            cond: (selected) =>
              Boolean(selected?.serverGroup) && ['active', 'stopped'].includes(selected?.state),
            disabledTooltip: (selected) =>
              !selected?.serverGroup
                ? ACTION_LIMITATION_TOOLTIPS.VM_NOT_IN_SERVER_GROUP
                : !['active', 'stopped'].includes(selected?.state)
                  ? ACTION_LIMITATION_TOOLTIPS.VM_NOT_IN_ACTIVE_OR_STOPPED_STATE
                  : '',
            handleClick: (vm) => {
              setSelectedVms([vm])
              setShowRemoveServerGroupModal(true)
            },
            refreshAfterSuccess: false,
            permissionsPath: [
              'nova',
              'os_compute_api.os_server_groups.remove:POST:/servers/{server_id}/action',
            ],
          },
        ],
      },
    ],
    [userRole],
  )

  const columns = useMemo(
    () =>
      vmColumns({
        setErrorVm,
        setMigrationFailureVm,
        userRole,
        hosts,
        allTenants,
        blueprint,
        clusterHostGroups,
      }),
    [setErrorVm, setMigrationFailureVm, userRole, hosts, allTenants, blueprint, clusterHostGroups],
  )

  // Get Filter Configs from useVMFilterConfigs
  const filterConfigs = useVMFilterConfigs({
    allTenants,
    excludeFilters: ['host'],
    isAdmin: isAdmin(),
  })

  // Get initial search field and value from search params
  const { initialSearchField, initialSearchValue } = useMemo(() => {
    const initialSearchField = searchParams
      ? Object.keys(searchParams).find((k) =>
          VM_SEARCH_FIELDS.map((f) => f.value).includes(k as VM_SEARCH_FIELDS_ENUM),
        )
      : undefined
    const initialSearchValue =
      searchParams && initialSearchField
        ? initialSearchField === VM_SEARCH_FIELDS_ENUM.METADATA
          ? searchParams[initialSearchField].replace(':', '=')
          : searchParams[initialSearchField]
        : undefined
    return { initialSearchField, initialSearchValue }
  }, [searchParams])

  // Handle column header sort click → map column key to API sort key
  const onColumnSortChange = useCallback(
    (columnKey: string, direction: 'asc' | 'desc') => {
      const apiSortKey = VM_COLUMN_TO_SORT_KEY[columnKey]
      if (apiSortKey) {
        handleSort(apiSortKey, direction)
      }
    },
    [handleSort],
  )

  const onSearchHandler = useCallback(
    (params: ServerSideSearchParams) => {
      // Handle sort changes separately (avoid double API call)
      if (params?.sortKey !== sortKey || params?.sortDir !== sortDir) {
        handleSort(params?.sortKey, params?.sortDir)
        return
      }
      handleSearch(params?.searchField, params?.searchValue, params?.filters)
    },
    [handleSort, handleSearch, sortKey, sortDir],
  )

  const formatSearchValueCb = useCallback((searchField: string, searchValue: string) => {
    if (searchField === VM_SEARCH_FIELDS_ENUM.METADATA) return searchValue.replace('=', ':')

    return searchValue
  }, [])

  // Determine if any VM is in 'deleting' or 'deleted' state to decide whether to trigger complete refresh on polling
  // For VMs in deleting/deleted state, we want to trigger an complete refresh on the next poll to update the entire list,
  // and remove the deleted VMs from the list
  const isAnyVMInDeletingState = useMemo(
    () => paginatedVMs.some((vm) => ['deleting', 'deleted'].includes(vm.status)),
    [paginatedVMs],
  )

  // Polling handler that triggers complete refresh if any VM is in deleting/deleted state, otherwise continues with regular polling
  const handlePolling = useCallback(async () => {
    if (isAnyVMInDeletingState) {
      await handleRefresh(false)
    } else {
      await activePoll()
    }
  }, [activePoll, handleRefresh, isAnyVMInDeletingState])

  const showOwnersToggle =
    // CD Admin and Tenant admin don't have
    // cross-tenant API privileges
    // that should be able to see other users' VMs
    // and add in the legacy SSU when not restricted_ssu
    (isSelfServiceRole() && !session?.features?.features?.isolated_ssu) ||
    (!hasPermissions(['nova', 'os_compute_api.servers.index.get_all_tenants:GET:/servers']) &&
      hasPermissions(['nova', 'os_compute_api.servers.create:POST:/servers']) &&
      userHasRole(['tenant_admin', 'customer_domain_admin']))

  const hasCrossTenantPermissions =
    userRole === 'admin' ||
    (hasPermissions(['nova', 'os_compute_api.servers.index.get_all_tenants:GET:/servers']) &&
      !['member', 'reader'].includes(userRole))

  const seeOwnVmsOnly = !hasCrossTenantPermissions && !allOwners

  return (
    <>
      <PollingData
        hidden
        loading={activePolling}
        // @ts-ignore
        onReload={handlePolling}
        refreshDuration={1000 * 30}
        pause={pausePolling}
      />
      <PollingData
        hidden
        loading={pollingMigrations}
        // @ts-ignore
        onReload={pollMigrations}
        refreshDuration={1000 * 30}
        pause={pausePolling}
      />
      {/* {isHostDetailsPage && (
        <QuotaUsageData vms={vmStateCount} loadingVMs={isStatewiseVMsLoading} />
      )} */}
      {!isHostDetailsPage && (
        <CreateVirtualMachineModal
          addRoute={routeManager.routes.openstack.deployVm}
          reloadVirtualMachines={handleRefresh}
        />
      )}
      {showDeleteDialog && (
        <DeleteVirtualMachineDialog onClose={() => setShowDeleteDialog(false)} rows={selectedVms} />
      )}
      {showPowerDialog && (
        <PowerVirtualMachineDialog
          onClose={() => setShowPowerDialog(false)}
          rows={selectedVms}
          action={selectedPowerAction}
          type={rebootType}
        />
      )}
      {showRenameDialog && (
        <RenameVirtualMachineDialog onClose={() => setShowRenameDialog(false)} rows={selectedVms} />
      )}
      {showSnapshotDialog && (
        <SnapshotVirtualMachineDialog
          onClose={() => setShowSnapshotDialog(false)}
          rows={selectedVms}
        />
      )}
      {showRebuildDialog && (
        <RebuildVirtualMachineDialog
          onClose={() => setShowRebuildDialog(false)}
          rows={selectedVms}
        />
      )}
      {showAddFixedIpModal && (
        <AddFixedIpModal onClose={() => setShowAddFixedIpModal(false)} rows={selectedVms} />
      )}
      {showRemoveFixedIpDialog && (
        <RemoveFixedIpDialog onClose={() => setShowRemoveFixedIpDialog(false)} rows={selectedVms} />
      )}
      {showAddFloatingIpDialog && (
        <AddFloatingIpDialog onClose={() => setShowAddFloatingIpDialog(false)} rows={selectedVms} />
      )}
      {showRemoveFloatingIpDialog && (
        <RemoveFloatingIpDialog
          onClose={() => setShowRemoveFloatingIpDialog(false)}
          rows={selectedVms}
        />
      )}
      {showEditVmSecurityGroupsModal && (
        <EditVmSecurityGroupsModal
          onClose={() => setShowEditVmSecurityGroupsModal(false)}
          rows={selectedVms}
        />
      )}
      {showEditVmPortSecurityModal && (
        <EditVmPortSecurityModal
          onClose={() => setShowEditVmPortSecurityModal(false)}
          rows={selectedVms}
        />
      )}
      {showEditVmMetadataModal && (
        <EditVmMetadataModal onClose={() => setShowEditVmMetadataModal(false)} rows={selectedVms} />
      )}
      {showResizeDialog && (
        <ResizeVirtualMachineDialog onClose={() => setShowResizeDialog(false)} rows={selectedVms} />
      )}
      {showConfirmResizeDialog && (
        <ConfirmResizeDialog onClose={() => setShowConfirmResizeDialog(false)} rows={selectedVms} />
      )}
      {showMigrateDialog && (
        <MigrateVirtualMachineDialog
          onClose={() => setShowMigrateDialog(false)}
          rows={selectedVms}
        />
      )}
      {showMigrationPriorityDialog && (
        <UpdateMigrationPriorityDialog
          onClose={() => setShowMigrationPriorityDialog(false)}
          rows={selectedVms}
        />
      )}
      {showChangeTenantDialog && (
        <ChangeTenantDialog
          onClose={(success) => {
            setShowChangeTenantDialog(false)
            if (success) handleRefresh(true)
          }}
          rows={selectedVms}
        />
      )}
      {showManageVolumesModal && (
        <ManageVolumesModal
          onClose={() => setShowManageVolumesModal(false)}
          rows={selectedVms}
          isOpenModal={showManageVolumesModal}
        />
      )}
      {showRescueDialog && (
        <RescueVmDialog onClose={() => setShowRescueDialog(false)} rows={selectedVms} />
      )}
      {showUnrescueDialog && (
        <UnrescueVmDialog onClose={() => setShowUnrescueDialog(false)} rows={selectedVms} />
      )}
      {showMountISODialog && (
        <MountISODialog
          onClose={(success) => {
            setShowMountISODialog(false)
            if (success) {
              onRefresh()
            }
          }}
          rows={selectedVms}
        />
      )}
      {showUnmountISODialog && (
        <UnmountISODialog
          onClose={(success) => {
            setShowUnmountISODialog(false)
            if (success) {
              onRefresh()
            }
          }}
          rows={selectedVms}
        />
      )}
      {showHotplugDialog && (
        <HotplugVmDialog onClose={() => setShowHotplugDialog(false)} rows={selectedVms} />
      )}
      {/* CLVM-FR-015: always mounted — visibility is derived from the `cloneRoute` URL match, not
          a local boolean. `cloneRoute` switches to `cloneVmFromHostDetails` when this list is
          host-scoped. `rows` prefers `cloneTargetVm` (looked up via the `vmId` query param from
          the Redux-cached VM list) over `selectedVms` — clicking Clone always remounts this
          component (each route gets its own key in the app's Switch), which wipes `selectedVms`
          before this modal's route-match gate ever opens; `cloneTargetVm` survives that remount
          since it's derived fresh from the URL + Redux cache on every render, not local state. */}
      <CloneVirtualMachineModal
        cloneRoute={cloneRoute}
        onClose={() => setSelectedVms(null)}
        rows={cloneTargetVm ? [cloneTargetVm] : selectedVms}
      />
      {showCustomLeaseModal && (
        <CustomVmLeaseModal onClose={() => setShowCustomLeaseModal(false)} rows={selectedVms} />
      )}
      {showAddServerGroupModal && (
        <AddServerGroupDialog
          onClose={() => setShowAddServerGroupModal(false)}
          rows={selectedVms}
        />
      )}
      {showRemoveServerGroupModal && (
        <RemoveServerGroupDialog
          onClose={() => setShowRemoveServerGroupModal(false)}
          rows={selectedVms}
        />
      )}
      {showManageVolumeTerminationModal && (
        <ManageVolumeTerminationModal
          onClose={() => setShowManageVolumeTerminationModal(false)}
          rows={selectedVms}
        />
      )}
      {showAssignFdDialog && selectedVms?.length > 0 && (
        <AssignStretchedClusterDialog
          onClose={(success) => {
            setShowAssignFdDialog(false)
            if (success) onRefresh()
          }}
          rows={selectedVms}
        />
      )}
      {showRemoveFdDialog && selectedVms?.length > 0 && (
        <RemoveStretchedClusterDialog
          onClose={(success) => {
            setShowRemoveFdDialog(false)
            if (success) onRefresh()
          }}
          rows={selectedVms}
        />
      )}
      {errorVm && (
        <VmErrorModal vmId={errorVm} hostName={errorVm} onClose={() => setErrorVm(null)} />
      )}
      {migrationFailureVm && (
        <FailedMigrationReasonDialog
          vm={migrationFailureVm}
          onClose={() => setMigrationFailureVm(null)}
        />
      )}
      <VmConsoleLoadingDialog
        open={consoleState.open}
        loading={consoleState.loading}
        error={consoleState.error}
        vmName={consoleState.vmName}
        onClose={() => setConsoleState((prev) => ({ ...prev, open: false }))}
      />
      <div className={classes.tableWrapper}>
        {hasCrossTenantPermissions ? (
          <ToggleSwitch
            active={allTenants}
            onClick={handleAllTenantsToggle}
            label="View VMs in All Tenants"
            className={classes.floatingToggle}
          />
        ) : showOwnersToggle ? (
          <ToggleSwitch
            active={allOwners}
            onClick={handleAllOwnersToggle}
            label="View VMs for All Owners"
            className={classes.floatingToggle}
          />
        ) : null}
        <ListContainer<ModelDataKey, SelectorModel>
          listPermsKeyPath={['nova', 'os_compute_api.servers.detail:GET:/servers/detail']}
          createPermsKeyPath={['nova', 'os_compute_api.servers.create:POST:/servers']}
          editPermsKeyPath={['nova', 'os_compute_api.servers.update:PUT:/servers/{server_id}']}
          deletePermsKeyPath={['nova', 'os_compute_api.servers.delete:DELETE:/servers/{server_id}']}
          dataKey={DataKeys.OpenstackVirtualMachines}
          emptyContent={seeOwnVmsOnly ? 'You have no VMs on this tenant' : undefined}
          showBreadcrumbs={false}
          // searchTargets={searchTargets}
          // searchFunctions={searchFunctions}
          searchBoxLabel="Page Search"
          disableSearch={true}
          uniqueIdentifier="id"
          loading={activeLoading}
          loadingMessage={activeMessage}
          onRefresh={onRefresh}
          // Pass only current page data - search will work on these items only
          data={paginatedVMs}
          columns={columns}
          getParamsUpdater={getParamsUpdater}
          // @ts-ignore
          deleteAction={deleteVirtualMachine}
          deleteCond={(vms) => {
            return canDeleteVms(vms, hostsRef.current)
          }}
          deleteDisabledInfo={ACTION_LIMITATION_TOOLTIPS.CANNOT_DELETE_ACTIVE_VM}
          // This requires DeleteDialogComponent to also trigger list deselection
          DeleteDialogComponent={DeleteVirtualMachineDialog}
          postDeleteAction={onRefresh}
          batchActions={batchActions}
          dropdownBatchActions={dropdownBatchActions}
          // Some bug when rowMenuItems is active?
          rowMenuItems={rowMenuItems}
          rowMenuOffset={{ vertical: 30 }}
          // refreshing={activeRefreshing}
          showRowMenuForSingleRowActions
          multiSelection
          iconOnly
          rowIsSelectableFn={(vm) => !(vm.state === 'deleted' || vm.taskState === 'deleting')}
          // Disable client-side pagination since we're using server-side
          disablePagination={true}
          searchComponent={
            <ServerSideSearch
              searchFields={VM_SEARCH_FIELDS}
              sortOptions={VM_SORT_OPTIONS}
              onSearch={onSearchHandler}
              filterConfigs={filterConfigs}
              onClear={handleClearSearch}
              isLoading={activeLoading || isPaginationLoading}
              initialParams={{
                searchField: initialSearchField,
                searchValue: initialSearchValue,
                filters: filterParamsForUI as unknown as Record<string, string>,
                sortKey,
                sortDir,
              }}
              defaultSortKey="created_at"
              defaultSortDir="desc"
              formatSearchValue={formatSearchValueCb}
            />
          }
          onManageColumnsToggleReady={(toggle) => {
            manageColumnsToggleRef.current = toggle
          }}
          clearSelectionRef={clearSelectionRef}
          // Custom server-side pagination
          CustomPaginationComponent={() => (
            <VMServerSidePagination
              dataKey={
                allTenants
                  ? DataKeys.AllOpenstackVirtualMachines
                  : seeOwnVmsOnly
                    ? DataKeys.SelfOpenstackVirtualMachines
                    : DataKeys.OpenstackVirtualMachines
              }
              currentPage={currentPage}
              pageSize={pageSize}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              totalCount={totalCount}
              hasNextPage={hasNextPage}
              nextMarker={pageMarkers[currentPage + 1]}
            />
          )}
          {...pick(listTablePrefs, params)}
          controlledSorting
          orderBy={sortKey ? VM_SORT_KEY_TO_COLUMN[sortKey] || sortKey : undefined}
          orderDirection={sortDir}
          onSortChange={onColumnSortChange}
        />
      </div>
    </>
  )
}

const useStyles = makeStyles<Theme>((theme) => ({
  addresses: {
    display: 'grid',
    gap: 8,
  },
  l2IpRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  l2IpIcon: {
    fontSize: '14px',
    color: '#9E9E9E',
    cursor: 'help',
    display: 'flex',
  },

  dropdownAction: {
    background: theme.components.dropdown.background,
    border: 'none',
    '& .button-text': {
      color: theme.components.dropdown.color,
      justifyContent: 'start',
    },
  },
  nowrap: {
    whiteSpace: 'nowrap',
  },
  inlineBlock: {
    display: 'inline-block',
  },
  flavorTooltip: {
    padding: 8,
  },
  linkAppearance: {
    color: theme.palette.primary.main,
  },
  toggleSwitch: {
    marginBottom: '8px',
  },
  tableWrapper: {
    position: 'relative',
  },
  floatingToggle: {
    position: 'absolute',
    top: '-50px',
    right: 0,
    zIndex: 10,
  },
  volumesCell: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 4,
  },
  volumesExpander: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '4px 0',
    color: theme.palette.grey[600],
  },
  volumesExpanderText: {
    fontWeight: 600,
    color: theme.palette.grey[600],
    whiteSpace: 'nowrap' as const,
  },
  volumesExpanderIcon: {
    color: theme.palette.grey[600],
    fontSize: '16px !important',
  },
}))
